import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  normalizePayeeName,
  extractZellePayeeKey,
  zellePayeeKeysMatch,
  isZelleDescription,
  ruleKeyForDescription
} from '../utils/zellePayee';
import {
  extractMatchKey,
  descriptionMatchesRule,
  SCHEDULE_E_CATEGORIES,
  BUDGET_CATEGORIES,
  isIncomeCategory
} from '../utils/statementRules';
import { looksLikeMortgageDescription } from '../utils/mortgageHelpers';
import './BankStatementAnalyzer.css';

const BankStatementAnalyzer = ({ onClose, onTransactionsCategorized }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [duplicateTransactions, setDuplicateTransactions] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deletedTransactions, setDeletedTransactions] = useState([]);
  const [showDeletedTransactions, setShowDeletedTransactions] = useState(false);
  const [recurringTypes, setRecurringTypes] = useState({});
  const [newBillProperties, setNewBillProperties] = useState({});
  const [newBillIrsTypes, setNewBillIrsTypes] = useState({});
  const [incomeTypes, setIncomeTypes] = useState({});
  const [properties, setProperties] = useState([]);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [savingsAccounts, setSavingsAccounts] = useState({});
  const [smartAssignments, setSmartAssignments] = useState({});
  const [learningRules, setLearningRules] = useState({});
  const [autoCategorizedCount, setAutoCategorizedCount] = useState(0);
  const [unknownTransactions, setUnknownTransactions] = useState([]);
  // Rules UI state
  const [showRules, setShowRules] = useState(false);
  const [rulesSearch, setRulesSearch] = useState('');
  // Property selection for rental income (stores transaction data)
  const [rentalIncomePropertySelection, setRentalIncomePropertySelection] = useState(null);
  // Duplicate review modal state
  const [duplicateReviewModal, setDuplicateReviewModal] = useState(null); // { duplicates, transactionsToSave, headers }
  const [isMerging, setIsMerging] = useState(false);
  // Apply to similar modal + queued transactions (Option A: review → apply & queue)
  const [applySimilarModal, setApplySimilarModal] = useState(null); // { matches, category, sourceTx, indices }
  const [queuedTransactions, setQueuedTransactions] = useState([]);
  const [showQueuedSection, setShowQueuedSection] = useState(true);
  const [transactionFilter, setTransactionFilter] = useState('all'); // show full list; use Need review filter to focus
  const learningRulesRef = React.useRef(learningRules);
  learningRulesRef.current = learningRules;

  // Parser heuristics set `category`; only `userCategory` means the user confirmed or a saved rule applied.
  const needsUserReview = (tx) => !tx?.userCategory;
  const countNeedsReview = (list) => (list || []).filter(needsUserReview).length;
  const [transactionSearchText, setTransactionSearchText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState('idle'); // idle | upload | processing
  const [mortgageProfiles, setMortgageProfiles] = useState([]);
  const [mortgageMatches, setMortgageMatches] = useState([]); // { index, profileId, estimated_split }
  const [mortgageSectionCollapsed, setMortgageSectionCollapsed] = useState(false);

  // IRS Deduction Types for tax-deductible expenses
  const irsDeductionTypes = {
    'property_taxes': { name: 'Property Taxes (SALT)', code: 'A' },
    'mortgage_interest': { name: 'Mortgage Interest', code: 'A' },
    'charitable_contributions': { name: 'Charitable Contributions', code: 'A' },
    'business_use_of_home': { name: 'Business Use of Home', code: '8829' },
    'rental_expenses': { name: 'Rental Expenses', code: 'E' },
    'repairs_maintenance': { name: 'Repairs & Maintenance', code: 'E' },
    'utilities': { name: 'Utilities', code: 'E' },
    'insurance': { name: 'Insurance', code: 'E' },
    'property_taxes': { name: 'Property Taxes', code: 'E' },
    'mortgage_interest': { name: 'Mortgage Interest', code: 'E' },
    'depreciation': { name: 'Depreciation', code: 'E' },
    'professional_services': { name: 'Professional Services', code: 'E' },
    'travel': { name: 'Travel', code: 'E' },
    'office_supplies': { name: 'Office Supplies', code: 'E' },
    'other_business': { name: 'Other Business Expenses', code: 'E' },
    'child_care': { name: 'Child Care Expenses', code: '2441' },
    'child_education': { name: 'Child Education (Private School)', code: '529/ESA' },
    'dependent_care_fsa': { name: 'Dependent Care FSA', code: 'FSA' },
    'education_expenses': { name: 'Education Expenses', code: '1098-T' },
    'student_loan_interest': { name: 'Student Loan Interest', code: '1098-E' }
  };

  // Income Types for Zelle received payments
  const incomeTypeOptions = {
    'rental_income': { name: 'Rental Income', icon: '🏠' },
    'business_income': { name: 'Business Income', icon: '💼' },
    'consulting_income': { name: 'Consulting Income', icon: '💡' },
    'investment_income': { name: 'Investment Income', icon: '📈' },
    'other_income': { name: 'Other Income', icon: '💰' }
  };

  // Helper function to normalize payee names (matching backend logic)
  // For Zelle rules use ruleKeyForDescription() instead — strips confirmation numbers

  // Fetch properties and accounts on component mount
  React.useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get('/api/properties', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        if (response.data.success && response.data.data) {
          console.log('✅ Properties loaded successfully:', response.data.data);
          setProperties(response.data.data);
        } else {
          console.warn('No properties found or invalid response:', response.data);
          setProperties([]); // Set empty array as fallback
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setProperties([]); // Set empty array as fallback
        toast.error('Failed to load properties. Please refresh the page.');
      } finally {
        setPropertiesLoaded(true);
      }
    };

    const fetchAccounts = async () => {
      try {
        const response = await axios.get('/api/accounts', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        if (response.data.success && response.data.data) {
          console.log('✅ Accounts loaded successfully:', response.data.data);
          setAccounts(response.data.data);
        } else {
          console.warn('No accounts found or invalid response:', response.data);
          setAccounts([]); // Set empty array as fallback
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setAccounts([]); // Set empty array as fallback
        toast.error('Failed to load accounts. Please refresh the page.');
      } finally {
        setAccountsLoaded(true);
      }
    };

    fetchProperties();
    fetchAccounts();
    
    // Load smart assignments from localStorage
    const savedAssignments = JSON.parse(localStorage.getItem('smartAssignments') || '{}');
    setSmartAssignments(savedAssignments);
    
    // Load learning rules from server (this will also sync localStorage)
    loadServerRules();
  }, []);

  // Load server rules when Rules panel is opened
  React.useEffect(() => {
    loadServerRules();
  }, []);

  React.useEffect(() => {
    if (showRules) loadServerRules();
  }, [showRules]);

  // Keep insights "Need review" count in sync as user labels transactions
  React.useEffect(() => {
    if (!analysisResults?.allTransactions) return;
    setUnknownTransactions(analysisResults.allTransactions.filter(needsUserReview));
  }, [analysisResults?.allTransactions]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    // Validate file types
    const validFiles = acceptedFiles.filter(file => 
      file.name.toLowerCase().endsWith('.csv') || file.name.toLowerCase().endsWith('.pdf')
    );

    if (validFiles.length === 0) {
      toast.error('Please upload CSV or PDF files only');
      return;
    }

    setUploadedFiles(validFiles);
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setAnalysisPhase('upload');

    try {
      // Create FormData for multiple files
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append('file', file);
      });

      // Warn if over limit (backend accepts 48)
      if (validFiles.length > 48) {
        toast.error(`Only the first 48 files will be analyzed. You selected ${validFiles.length}. Consider splitting into batches.`);
        validFiles.splice(48);
      }

      // Upload and analyze (5 min timeout for large batches)
      const response = await axios.post('/api/expenses/analyze-statements', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        timeout: 300000, // 5 minutes for full-year analysis
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setAnalysisProgress(Math.min(progress, 85));
          if (progress >= 99) setAnalysisPhase('processing');
        }
      });

      if (response.data.success) {
        console.log('🔍 Frontend received analysis results:', response.data.data);
        const { data, processedStatements, mergeInfo } = response.data;
        
        // Apply learning rules for auto-categorization
        const autoCategorizedTransactions = applyLearningRules(data.allTransactions || []);
        
        const updatedResults = {
          ...data,
          allTransactions: autoCategorizedTransactions,
          processedStatements: processedStatements,
          mergeInfo: mergeInfo
        };
        
        setAnalysisResults(updatedResults);
        setQueuedTransactions([]); // Clear queue when new statements are analyzed
        setTransactionFilter('all');
        setShowAnalytics(false);

        // Group duplicate transactions for batch categorization
        const duplicates = groupDuplicateTransactions(autoCategorizedTransactions);
        setDuplicateTransactions(duplicates);

        // Mortgage payments → top section (link to rental mortgage calculator profiles)
        try {
          const mortRes = await axios.post('/api/mortgage/match-transactions', {
            transactions: autoCategorizedTransactions
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
          });
          if (mortRes.data?.success) {
            setMortgageProfiles(mortRes.data.data.profiles || []);
            setMortgageMatches(mortRes.data.data.matches || []);
            if ((mortRes.data.data.review_due_count || 0) > 0) {
              toast(`Mortgage check-in due for ${mortRes.data.data.review_due_count} property — open Mortgage Calculator`);
            }
          } else {
            setMortgageMatches([]);
            setMortgageProfiles([]);
          }
        } catch {
          // Fallback: local detect only
          const local = [];
          autoCategorizedTransactions.forEach((t, index) => {
            if (looksLikeMortgageDescription(t.description)) {
              local.push({ index, description: t.description, date: t.date, amount: Math.abs(t.amount), profile_id: null });
            }
          });
          setMortgageMatches(local);
          setMortgageProfiles([]);
        }
        
        // Show multi-statement processing summary
        const totalFiles = mergeInfo?.totalStatements ?? 0;
        const failedCount = mergeInfo?.fileErrors?.length ?? 0;
        const successCount = totalFiles - failedCount;
        if (totalFiles > 1) {
          if (failedCount > 0) {
            toast.warning(`📊 Analyzed ${successCount} of ${totalFiles} statements. ${failedCount} failed.`);
          } else {
            toast.success(`📊 Processed ${totalFiles} statements with intelligent merging!`);
          }
          if (mergeInfo.duplicateTransactionsRemoved > 0) {
            toast(`🔄 Removed ${mergeInfo.duplicateTransactionsRemoved} duplicate transactions`);
          }
        }
        if (mergeInfo?.fileErrors?.length > 0) {
          mergeInfo.fileErrors.forEach(({ fileName, error }) => {
            toast.error(`❌ ${fileName}: ${error}`);
          });
        }
        
        // Show auto-categorization summary
        if (autoCategorizedCount > 0) {
          toast.success(`🤖 Auto-categorized ${autoCategorizedCount} transactions based on your previous choices!`);
        }
        
        if (unknownTransactions.length > 0) {
          toast(`❓ ${unknownTransactions.length} transactions need your attention for categorization.`);
        }
        
        toast.success('Bank statements analyzed successfully!');
      } else {
        // Handle specific error cases
        if (response.data.error === 'Duplicate statements detected') {
          const duplicateFiles = response.data.duplicates?.map(d => d.fileName).join(', ') || 'unknown files';
          toast.error(`❌ Duplicate files detected: ${duplicateFiles}. Please rename your files and try again.`);
        } else {
          toast.error(response.data.message || 'Analysis failed');
        }
        setAnalysisResults(null);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Try fewer files (e.g. 12–24 at a time) or split into batches.');
      } else if (error.response?.data?.error === 'Duplicate statements detected') {
        const duplicateFiles = error.response.data.duplicates?.map(d => d.fileName).join(', ') || 'unknown files';
        toast.error(`❌ Duplicate files detected: ${duplicateFiles}. Please rename your files and try again.`);
      } else {
        toast.error('Failed to analyze bank statements: ' + (error.response?.data?.error || error.message || 'Unknown error'));
      }
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
      setAnalysisPhase('idle');
    }
  }, []);

  // Normalize description for grouping/matching - strip dates (MMDDYY), asterisks, refs
  const normalizeForRecurringMatch = (text) => {
    if (!text) return '';
    return String(text).toLowerCase()
      .replace(/\d{2}\/\d{2}\/\d{4}/g, '')
      .replace(/\d{2}\/\d{2}/g, '')
      .replace(/\b(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])\d{2}\b/g, '') // MMDDYY
      .replace(/\b(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])\d{4}\b/g, '') // MMDDYYYY
      .replace(/\d{10,}/g, '')
      .replace(/\*\d+/g, '')
      .replace(/\*+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Match ATM withdrawal - common patterns: ATM WITHDRAWAL, ATM DEBIT, ATM CASH, etc.
  const isAtmWithdrawal = (desc) => {
    if (!desc) return false;
    const d = String(desc).toUpperCase();
    return /ATM\s+(WITHDRAWAL|DEBIT|CASH|W\/D)/i.test(d) || /ATM\s*WITHDRAWAL/i.test(d) || /^ATM\s/i.test(d);
  };

  // Match likely W-2 / payroll deposits - DIRECT DEP, PAYROLL, ACH CREDIT, etc.
  const isLikelyW2Income = (tx) => {
    if (!tx || (tx.amount || 0) <= 0) return false;
    const d = String(tx.description || '').toUpperCase();
    return /DIRECT\s*DEP|PAYROLL|ACH\s*CREDIT|DEPOSIT|PAY\s*DEP|SALARY|W-?2|WAGE/i.test(d) ||
      (d.includes('DEPOSIT') && d.includes('CREDIT'));
  };

  // Filter transactions for display (search + quick filters)
  const getFilteredTransactions = () => {
    const list = analysisResults?.allTransactions || [];
    if (list.length === 0) return [];
    const search = (transactionSearchText || '').trim().toLowerCase();
    return list
      .map((t, i) => ({ t, originalIndex: i }))
      .filter(({ t }) => {
        if (transactionFilter === 'atm') return isAtmWithdrawal(t.description);
        if (transactionFilter === 'w2') {
          const cat = t.userCategory || t.category;
          return cat === 'w2_income' || isLikelyW2Income(t);
        }
        if (transactionFilter === 'uncategorized') {
          return needsUserReview(t) && t.userCategory !== 'ignore';
        }
        if (search) {
          const desc = (t.description || '').toLowerCase();
          const amt = String(t.amount || '');
          return desc.includes(search) || amt.includes(search);
        }
        return true;
      });
  };

  // Progress stats for sticky header
  const getProgressStats = () => {
    const list = analysisResults?.allTransactions || [];
    const uncategorized = countNeedsReview(list);
    const categorizedInList = list.filter(t => t.userCategory && t.userCategory !== 'ignore').length;
    const queued = queuedTransactions.filter(t => t.userCategory && t.userCategory !== 'ignore').length;
    const importCount = categorizedInList + queued;
    const total = list.length;
    const duplicateGroups = duplicateTransactions.filter(g => !g.applied).length;
    return { total, uncategorized, categorizedInList, queued, importCount, duplicateGroups };
  };

  // Save unified statement rule + apply to all matching payees in this import
  const saveStatementRule = async (transaction, { taxable, category, propertyId, disposition }) => {
    if (!transaction?.description || !category) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    const matchKey = extractMatchKey(transaction.description);
    const disp = disposition || (category === 'ignore' || category === 'transfer_ignore'
      ? 'transfer_ignore'
      : isIncomeCategory(category)
        ? 'income'
        : taxable
          ? 'expense'
          : 'personal_budget');

    try {
      await axios.post('/api/expenses/statement-rules', {
        match_key: matchKey,
        display_name: transaction.description,
        description: transaction.description,
        taxable: taxable && category !== 'ignore',
        disposition: disp,
        category,
        budget_category: taxable ? null : category,
        property_id: propertyId || null,
        amount: Math.abs(transaction.amount),
        is_recurring: category !== 'ignore'
      }, { headers });

      setLearningRules(prev => ({
        ...prev,
        [matchKey]: {
          displayName: transaction.description,
          category,
          propertyId: propertyId || null,
          taxable,
          disposition: disp,
          ruleType: 'statement'
        }
      }));
    } catch (e) {
      console.warn('Save statement rule failed:', e);
    }
  };

  const applyRuleToSimilarInList = (sourceTx, patch) => {
    if (!analysisResults?.allTransactions) return;
    const key = extractMatchKey(sourceTx.description);
    const updated = analysisResults.allTransactions.map(t => {
      if (!descriptionMatchesRule(key, t.description)) return t;
      return {
        ...t,
        ...patch,
        userCategory: patch.userCategory || patch.category,
        category: patch.category || patch.userCategory
      };
    });
    setAnalysisResults({ ...analysisResults, allTransactions: updated });
  };

  const handleStatementLabel = async (index, taxableChoice, category, propertyId) => {
    if (!analysisResults?.allTransactions) return;
    const tx = analysisResults.allTransactions[index];
    if (!category && taxableChoice !== 'ignore') return;

    if (taxableChoice === 'ignore') {
      await saveStatementRule(tx, { taxable: false, category: 'ignore', propertyId: null, disposition: 'transfer_ignore' });
      const updated = [...analysisResults.allTransactions];
      updated[index] = { ...updated[index], userCategory: 'ignore', category: 'ignore', isTaxable: false };
      setAnalysisResults({ ...analysisResults, allTransactions: updated });
      toast.success('Transfer/ignore rule saved — line marked; future imports will skip matches');
      return;
    }

    const isTaxable = taxableChoice === 'yes';
    const isIncome = isIncomeCategory(category);
    const patch = {
      isTaxable,
      isPersonalBudget: !isTaxable,
      propertyId: propertyId || null,
      recurringProperty: propertyId || null,
      newBillProperty: propertyId || null,
      recurringTaxDeductible: isTaxable,
      userCategory: category,
      category,
      isRentalIncome: category === 'rental_income'
    };

    await saveStatementRule(tx, { taxable: isTaxable, category, propertyId, disposition: isIncome ? 'income' : undefined });

    const updated = [...analysisResults.allTransactions];
    updated[index] = { ...updated[index], ...patch };
    setAnalysisResults({ ...analysisResults, allTransactions: updated });
    setHasUnsavedChanges(true);
    toast.success(`Rule saved for "${extractMatchKey(tx.description)}" — use Apply to similar for other lines from this payee`);
  };

  // Legacy auto-save wrapper
  const autoSaveRule = async (transaction, category, extras = {}) => {
    if (!transaction?.description || !category || category === 'ignore') return;
    const propId = extras.propertyId || extras.recurringProperty || extras.newBillProperty || null;
    const taxable = !['atm_withdrawal', 'savings', 'groceries', 'dining', 'subscriptions', 'personal_budget'].includes(category);
    await saveStatementRule(transaction, { taxable, category, propertyId: propId });
  };

  const jumpToNextUncategorized = () => {
    const list = analysisResults?.allTransactions || [];
    const idx = list.findIndex(needsUserReview);
    if (idx === -1) {
      toast.success('All transactions categorized!');
      return;
    }
    const el = document.getElementById(`txn-row-${idx}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('txn-highlight');
      setTimeout(() => el.classList.remove('txn-highlight'), 2000);
    }
  };

  // Group by payee + amount (e.g. same Zelle tenant same rent, or 12× TUBE $22.99)
  const groupDuplicateTransactions = (transactions) => {
    const groups = {};
    
    transactions.forEach(transaction => {
      if (transaction.description.toLowerCase().includes('beginning balance')) return;
      
      const payeeKey = extractMatchKey(transaction.description);
      if (!payeeKey || payeeKey.length < 2) return;
      const key = `${payeeKey}_${Math.abs(transaction.amount).toFixed(2)}`;
      
      if (!groups[key]) {
        groups[key] = {
          description: transaction.description,
          payeeKey,
          amount: Math.abs(transaction.amount),
          transactions: [],
          category: null
        };
      }
      groups[key].transactions.push(transaction);
    });
    
    // Return only groups with multiple transactions (same payee + same amount)
    return Object.values(groups).filter(group => group.transactions.length > 1);
  };

  // Handle individual transaction categorization
  const handleTransactionCategorization = (index, category) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    
    const updatedTransactions = [...analysisResults.allTransactions];
    const tx = updatedTransactions[index];
    updatedTransactions[index].userCategory = category;
    
    // If not recurring, clear the recurring type
    if (category !== 'recurring') {
      updatedTransactions[index].recurringType = null;
    }
    
    setAnalysisResults({
      ...analysisResults,
      allTransactions: updatedTransactions
    });
    
    setHasUnsavedChanges(true);
    autoSaveRule(tx, category, {
      propertyId: tx.propertyId,
      recurringProperty: tx.recurringProperty,
      newBillProperty: tx.newBillProperty
    });
  };

  // Filter out transaction IDs, dates (MMDDYY), ref numbers from description words
  const getMeaningfulWords = (desc, maxWords = 5) => {
    const all = (desc || '').trim().split(/\s+/);
    const filtered = all.map(w => {
      if (/^\*+/.test(w)) return w.replace(/^\*+/, ''); // ***********TUBE → TUBE
      return w;
    }).filter(w => {
      if (!w || w.length < 2) return false;
      if (/^\d+$/.test(w)) return false; // pure numbers
      if (/^\*+\d+$/.test(w)) return false; // ****1234
      if (/^(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])\d{2}$/.test(w)) return false; // MMDDYY
      if (/^(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])\d{4}$/.test(w)) return false; // MMDDYYYY
      if (w.replace(/\D/g, '').length > w.length * 0.6) return false; // mostly digits
      if (w.length >= 10 && /\d{8,}/.test(w)) return false; // long number strings
      return true;
    });
    return filtered.slice(0, maxWords);
  };

  // Open modal to review similar transactions, then Apply & queue (Option A)
  const handleTransactionRecategorizeSimilar = (index) => {
    if (!analysisResults?.allTransactions) return;
    const tx = analysisResults.allTransactions[index];
    const category = tx.userCategory || tx.category;
    if (!category) {
      toast.error('Select a category first, then click "Apply to similar"');
      return;
    }
    const matches = [];
    const indices = [];

    // Special case: ATM withdrawals - match all ATM withdrawals (locations/addresses vary per ATM)
    if (isAtmWithdrawal(tx.description)) {
      analysisResults.allTransactions.forEach((t, i) => {
        if (isAtmWithdrawal(t.description)) {
          matches.push(t);
          indices.push(i);
        }
      });
    } else {
      const payeeKey = extractMatchKey(tx.description);
      if (!payeeKey || payeeKey.length < 2) {
        toast.error('Could not extract payee pattern for matching');
        return;
      }
      const sourceAmount = Math.abs(parseFloat(tx.amount) || 0);
      const matchByAmount = category === 'recurring';
      const incomeCat = isIncomeCategory(category);
      analysisResults.allTransactions.forEach((t, i) => {
        if (!descriptionMatchesRule(payeeKey, t.description || '')) return;
        if (incomeCat) {
          const desc = (t.description || '').toLowerCase();
          const isDebit = Number(t.amount) > 0 || desc.includes('debit');
          if (isDebit) return;
        }
        const amountMatch = !matchByAmount || Math.abs(Math.abs(parseFloat(t.amount) || 0) - sourceAmount) < 0.01;
        if (amountMatch) {
          matches.push(t);
          indices.push(i);
        }
      });
    }
    if (matches.length === 0) {
      toast.error('No matching transactions found');
      return;
    }
    setApplySimilarModal({
      matches,
      category,
      sourceTx: tx,
      indices,
      removedIndices: new Set(), // User can remove individual matches (e.g. two recurring with same amount)
      propertyId: tx.propertyId || tx.recurringProperty || tx.newBillProperty || '',
      recurringType: tx.recurringType || '',
      recurringFrequency: tx.recurringFrequency || 'monthly',
      recurringIrsType: tx.recurringIrsType || '',
      newBillIrsType: tx.newBillIrsType || '',
      childIrsType: tx.childIrsType || '',
      savingsAccount: tx.savingsAccount || ''
    });
  };

  // Apply & queue: apply category to matches, remove from main list, add to queue
  const handleApplyAndQueue = () => {
    if (!applySimilarModal || !analysisResults?.allTransactions) return;
    const { matches, category, indices, removedIndices = new Set(), propertyId, recurringType, recurringFrequency, recurringIrsType, newBillIrsType, childIrsType, savingsAccount, sourceTx } = applySimilarModal;
    const propId = propertyId ? (typeof propertyId === 'string' ? parseInt(propertyId, 10) : propertyId) : null;
    const toApplyIndices = indices.filter((_, i) => !removedIndices.has(indices[i]));
    const updatedList = [...analysisResults.allTransactions];

    toApplyIndices.forEach((idx) => {
      const m = updatedList[idx];
      if (!m) return;
      if (category === 'rental_income' || category === 'w2_income') {
        const desc = (m.description || '').toLowerCase();
        const isDebit = Number(m.amount) > 0 || desc.includes('debit');
        if (isDebit) return;
      }
      const t = { ...m, userCategory: category, category };
      if (category === 'recurring') {
        t.recurringType = recurringType || m.recurringType;
        t.recurringFrequency = recurringFrequency || m.recurringFrequency || 'monthly';
        t.recurringProperty = propId ?? m.recurringProperty;
        t.recurringIrsType = recurringIrsType || m.recurringIrsType;
      } else if (category === 'rental_income') {
        t.propertyId = propId ?? m.propertyId;
        t.isTaxable = true;
        t.isRentalIncome = true;
      } else if (category === 'new_bill') {
        t.newBillProperty = propId ?? m.newBillProperty;
        t.newBillIrsType = newBillIrsType || m.newBillIrsType;
      } else if (category === 'child_expenses') {
        t.childIrsType = childIrsType || m.childIrsType;
      } else if (category === 'savings') {
        t.savingsAccount = savingsAccount || m.savingsAccount;
      }
      updatedList[idx] = t;
    });

    setAnalysisResults({ ...analysisResults, allTransactions: updatedList });
    setApplySimilarModal(null);
    setHasUnsavedChanges(true);

    if (toApplyIndices.length > 0 && sourceTx) {
      autoSaveRule(sourceTx, category, {
        propertyId: propId,
        recurringProperty: propId,
        newBillProperty: propId,
        recurringIrsType,
        newBillIrsType,
        childIrsType
      });
    }
    toast.success(`Labeled ${toApplyIndices.length} matching transaction(s) — continue reviewing the rest`);
  };

  // Handle recurring type selection for individual transactions
  const handleTransactionRecurringType = (index, recurringType) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    
    const updatedTransactions = [...analysisResults.allTransactions];
    updatedTransactions[index].recurringType = recurringType;
    
    setAnalysisResults({
      ...analysisResults,
      allTransactions: updatedTransactions
    });
    
    setHasUnsavedChanges(true);
  };

  // Handle individual recurring property selection
  const handleTransactionRecurringProperty = (index, propertyId) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    
    const updatedTransactions = [...analysisResults.allTransactions];
    updatedTransactions[index].recurringProperty = propertyId;
    
    setAnalysisResults({
      ...analysisResults,
      allTransactions: updatedTransactions
    });
    
    setHasUnsavedChanges(true);
  };

  // Handle recurring frequency selection (monthly, quarterly, semi_annually, yearly)
  const handleTransactionRecurringFrequency = (index, frequency) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    const updatedTransactions = [...analysisResults.allTransactions];
    updatedTransactions[index].recurringFrequency = frequency;
    setAnalysisResults({ ...analysisResults, allTransactions: updatedTransactions });
    setHasUnsavedChanges(true);
  };

  // Handle individual recurring IRS deduction type selection
  const handleTransactionRecurringIrsType = (index, irsType) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    
    const updatedTransactions = [...analysisResults.allTransactions];
    updatedTransactions[index].recurringIrsType = irsType;
    
    setAnalysisResults({
      ...analysisResults,
      allTransactions: updatedTransactions
    });
    
    setHasUnsavedChanges(true);
  };

  // Handle individual savings account selection
  const handleTransactionSavingsAccount = (index, accountId) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    
    const updatedTransactions = [...analysisResults.allTransactions];
    updatedTransactions[index].savingsAccount = accountId;
    
    setAnalysisResults({
      ...analysisResults,
      allTransactions: updatedTransactions
    });
    
    setHasUnsavedChanges(true);
  };

  // Handle individual child IRS deduction type selection
  const handleTransactionChildIrsType = (index, irsType) => {
    if (!analysisResults || !analysisResults.allTransactions) return;
    
    const updatedTransactions = [...analysisResults.allTransactions];
    updatedTransactions[index].childIrsType = irsType;
    
    setAnalysisResults({
      ...analysisResults,
      allTransactions: updatedTransactions
    });
    
    setHasUnsavedChanges(true);
  };

  // Get smart property suggestion based on transaction description and previous choices
  const getSmartPropertySuggestion = (description, billType) => {
    if (!description || !billType) return null;
    
    const key = `${billType.toLowerCase()}_${description.toLowerCase()}`;
    return smartAssignments[key] || null;
  };

  // Save smart assignment for future use
  const saveSmartAssignment = (description, billType, propertyId) => {
    if (!description || !billType || !propertyId) return;
    
    const key = `${billType.toLowerCase()}_${description.toLowerCase()}`;
    setSmartAssignments(prev => ({
      ...prev,
      [key]: propertyId
    }));
    
    // Also save to localStorage for persistence
    const savedAssignments = JSON.parse(localStorage.getItem('smartAssignments') || '{}');
    savedAssignments[key] = propertyId;
    localStorage.setItem('smartAssignments', JSON.stringify(savedAssignments));
  };

  // Create learning rule from transaction categorization
  const createLearningRule = (transaction, category, additionalData = {}) => {
    const description = transaction.description.toLowerCase();
    const amount = transaction.amount;
    
    // Create multiple rule variations for better matching
    const rules = [
      // Exact description match
      {
        type: 'exact_description',
        pattern: description,
        category,
        additionalData,
        confidence: 100
      },
      // Partial description match (first 3 words)
      {
        type: 'partial_description',
        pattern: description.split(' ').slice(0, 3).join(' '),
        category,
        additionalData,
        confidence: 85
      },
      // Amount range match (within 5%)
      {
        type: 'amount_range',
        pattern: `${Math.floor(amount * 0.95)}-${Math.ceil(amount * 1.05)}`,
        category,
        additionalData,
        confidence: 70
      },
      // Company name match (first word)
      {
        type: 'company_name',
        pattern: description.split(' ')[0],
        category,
        additionalData,
        confidence: 60
      }
    ];

    return rules;
  };

  // Apply learning rules to categorize transactions (server rules already set userCategory on analyze)
  const applyLearningRules = (transactions) => {
    let autoCategorized = 0;
    const rules = {
      ...JSON.parse(localStorage.getItem('learningRules') || '{}'),
      ...learningRulesRef.current
    };
    
    const updatedTransactions = transactions.map(transaction => {
      // Skip if already confirmed or server rule applied
      if (transaction.userCategory) {
        return transaction;
      }

      let bestMatch = null;
      let bestConfidence = 0;
      const txDesc = transaction.description.toLowerCase();
      const txNormalized = normalizePayeeName(transaction.description);
      const txAmount = transaction.amount;
      
      // Check for debit/credit indicators
      const isDebit = txAmount < 0 || txDesc.includes('debit');
      const isCredit = txAmount > 0 && !txDesc.includes('debit');

      // Check all learning rules
      Object.entries(rules).forEach(([ruleKey, rule]) => {
        let matches = false;
        let confidence = 0;

        // Handle server-loaded rules (ruleType: 'zelle' or 'general')
        if (rule.ruleType === 'statement' || rule.ruleType === 'zelle') {
          if (descriptionMatchesRule(ruleKey, transaction.description)) {
            matches = true;
            confidence = 95;
          }
        } else if (rule.ruleType === 'general') {
          if (isZelleDescription(transaction.description)) {
            if (zellePayeeKeysMatch(ruleKey, transaction.description)) {
              matches = true;
              confidence = 85;
            }
          } else if (txNormalized.includes(ruleKey) || ruleKey.includes(txNormalized)) {
            matches = true;
            confidence = 80;
          }
          // Also check amount if rule has amount
          if (rule.amount && Math.abs(Math.abs(txAmount) - Math.abs(rule.amount)) < 0.01) {
            confidence = Math.max(confidence, 90);
          }
        } else {
          // Handle legacy rule format (rule.type)
          switch (rule.type) {
            case 'exact_description':
              matches = txDesc === rule.pattern;
              confidence = rule.confidence || 70;
              break;
            case 'partial_description':
              matches = txDesc.startsWith(rule.pattern);
              confidence = rule.confidence || 70;
              break;
            case 'amount_range':
              const [min, max] = rule.pattern.split('-').map(Number);
              matches = txAmount >= min && txAmount <= max;
              confidence = rule.confidence || 70;
              break;
            case 'company_name':
              matches = txDesc.startsWith(rule.pattern);
              confidence = rule.confidence || 70;
              break;
          }
        }

        if (matches && confidence > bestConfidence) {
          // CRITICAL: Never apply rental_income rules to debits
          if (rule.category === 'rental_income' && isDebit) {
            // Skip this rule - debits should never be rental income
            return;
          }
          bestMatch = rule;
          bestConfidence = confidence;
        }
      });

      // Auto-categorize if confidence is high enough
      if (bestMatch && bestConfidence >= 70) {
        autoCategorized++;
        const category = bestMatch.category;
        const irsType = bestMatch.irsType || null;
        const extras = {};
        if (category === 'recurring' && irsType) extras.recurringIrsType = irsType;
        if (category === 'new_bill' && irsType) extras.newBillIrsType = irsType;
        if (category === 'child_expenses' && irsType) extras.childIrsType = irsType;
        
        return {
          ...transaction,
          userCategory: category,
          category,
          autoCategorized: true,
          confidence: bestConfidence,
          propertyId: bestMatch.propertyId || null,
          isTaxable: bestMatch.taxable !== false,
          isPersonalBudget: bestMatch.taxable === false,
          recurringTaxDeductible: bestMatch.taxable !== false,
          ...bestMatch.additionalData,
          ...extras
        };
      }
      return transaction;
    });

    setAutoCategorizedCount(autoCategorized);
    return updatedTransactions;
  };

  // Save learning rule to backend and localStorage
  const saveLearningRule = async (rule) => {
    try {
      // Save to backend
      await axios.post('/api/learning-rules', rule, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      // Save to localStorage for immediate use
      const savedRules = JSON.parse(localStorage.getItem('learningRules') || '{}');
      const ruleId = `${rule.type}_${rule.pattern}_${Date.now()}`;
      savedRules[ruleId] = rule;
      localStorage.setItem('learningRules', JSON.stringify(savedRules));

      // Update local state
      setLearningRules(prev => ({
        ...prev,
        [ruleId]: rule
      }));

      toast.success('Learning rule saved!');
    } catch (error) {
      console.error('Error saving learning rule:', error);
      toast.error('Failed to save learning rule');
    }
  };

  // Handle group categorization
  const handleGroupCategorization = (groupIndex, category) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].category = category;
    
    // If not recurring, clear the recurring type
    if (category !== 'recurring') {
      updatedGroups[groupIndex].recurringType = null;
    }
    
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Handle recurring type selection for groups
  const handleGroupRecurringType = (groupIndex, recurringType) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].recurringType = recurringType;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Handle new bill property selection
  const handleNewBillProperty = (index, propertyId) => {
    setNewBillProperties(prev => ({ ...prev, [index]: propertyId }));
    if (analysisResults?.allTransactions?.[index]) {
      const updated = [...analysisResults.allTransactions];
      updated[index] = { ...updated[index], newBillProperty: propertyId ? (typeof propertyId === 'string' ? parseInt(propertyId, 10) : propertyId) : null };
      setAnalysisResults({ ...analysisResults, allTransactions: updated });
    }
    setHasUnsavedChanges(true);
  };

  // Handle new bill IRS deduction type selection
  const handleNewBillIrsType = (index, irsType) => {
    setNewBillIrsTypes(prev => ({ ...prev, [index]: irsType }));
    if (analysisResults?.allTransactions?.[index]) {
      const updated = [...analysisResults.allTransactions];
      updated[index] = { ...updated[index], newBillIrsType: irsType || null };
      setAnalysisResults({ ...analysisResults, allTransactions: updated });
    }
    setHasUnsavedChanges(true);
  };

  // Handle group new bill property selection
  const handleGroupNewBillProperty = (groupIndex, propertyId) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].newBillProperty = propertyId;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
    
    // Save smart assignment for future use
    const group = updatedGroups[groupIndex];
    if (group && group.transactions && group.transactions.length > 0) {
      const firstTransaction = group.transactions[0];
      saveSmartAssignment(firstTransaction.description, 'new_bill', propertyId);
    }
  };

  // Handle group new bill IRS deduction type selection
  const handleGroupNewBillIrsType = (groupIndex, irsType) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].newBillIrsType = irsType;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Handle income type selection for individual transactions
  const handleIncomeType = (index, incomeType) => {
    setIncomeTypes(prev => ({
      ...prev,
      [index]: incomeType
    }));
    setHasUnsavedChanges(true);
  };

  // Handle group income type selection
  const handleGroupIncomeType = (groupIndex, incomeType) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].incomeType = incomeType;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Handle group savings account selection
  const handleGroupSavingsAccount = (groupIndex, accountId) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].savingsAccount = accountId;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Handle group child IRS deduction type selection
  const handleGroupChildIrsType = (groupIndex, irsType) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].childIrsType = irsType;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Handle recurring property selection
  const handleGroupRecurringProperty = (groupIndex, propertyId) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].recurringProperty = propertyId;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
    
    // Save smart assignment for future use
    const group = updatedGroups[groupIndex];
    if (group && group.transactions && group.transactions.length > 0) {
      const firstTransaction = group.transactions[0];
      saveSmartAssignment(firstTransaction.description, 'recurring', propertyId);
    }
  };

  // Handle recurring IRS deduction type selection
  const handleGroupRecurringIrsType = (groupIndex, irsType) => {
    const updatedGroups = [...duplicateTransactions];
    updatedGroups[groupIndex].recurringIrsType = irsType;
    setDuplicateTransactions(updatedGroups);
    setHasUnsavedChanges(true);
  };

  // Link statement mortgage lines to a calculator profile (full payment ≠ tax interest)
  const linkMortgageMatch = (matchIndex, profileId) => {
    const profile = mortgageProfiles.find(p => String(p.id) === String(profileId));
    if (!profile || !analysisResults?.allTransactions) return;
    const match = mortgageMatches[matchIndex];
    if (!match) return;

    const updated = [...analysisResults.allTransactions];
    const tx = updated[match.index];
    if (!tx) return;

    updated[match.index] = {
      ...tx,
      userCategory: 'mortgage_payment',
      category: 'mortgage_payment',
      propertyId: profile.property_id,
      isTaxable: false,
      linkedMortgageId: profile.id,
      mortgageSplit: match.estimated_split || null
    };
    setAnalysisResults({ ...analysisResults, allTransactions: updated });
    setMortgageMatches(prev => prev.map((m, i) =>
      i === matchIndex
        ? { ...m, profile_id: profile.id, property_id: profile.property_id, property_name: profile.property_name }
        : m
    ));
    setHasUnsavedChanges(true);
    toast.success(`Linked to ${profile.property_name} — interest comes from Mortgage Calculator, not the full ACH`);
  };

  const linkAllMortgageMatchesToProfile = (profileId) => {
    const profile = mortgageProfiles.find(p => String(p.id) === String(profileId));
    if (!profile || !analysisResults?.allTransactions) return;
    const updated = [...analysisResults.allTransactions];
    let count = 0;
    mortgageMatches.forEach(m => {
      if (!updated[m.index]) return;
      updated[m.index] = {
        ...updated[m.index],
        userCategory: 'mortgage_payment',
        category: 'mortgage_payment',
        propertyId: profile.property_id,
        isTaxable: false,
        linkedMortgageId: profile.id,
        mortgageSplit: m.estimated_split || null
      };
      count += 1;
    });
    setAnalysisResults({ ...analysisResults, allTransactions: updated });
    setMortgageMatches(prev => prev.map(m => ({
      ...m,
      profile_id: profile.id,
      property_id: profile.property_id,
      property_name: profile.property_name
    })));
    setHasUnsavedChanges(true);
    toast.success(`Linked ${count} mortgage payment(s) to ${profile.property_name}`);
  };

  // Apply batch group — same payee (+ same amount for recurring bills), never whole sheet
  const applyGroupCategorization = (groupIndex) => {
    const group = duplicateTransactions[groupIndex];
    if (!group || !group.category) {
      toast.error('Select a category first');
      return;
    }
    
    if (!analysisResults || !analysisResults.allTransactions) return;

    const payeeKey = group.payeeKey || extractMatchKey(group.transactions[0]?.description || '');
    if (!payeeKey) {
      toast.error('Could not identify payee for this group');
      return;
    }

    const matchByAmount = group.category === 'recurring';
    const isIncome = isIncomeCategory(group.category);
    const updatedBatch = [];
    const updatedList = [...analysisResults.allTransactions];

    updatedList.forEach((t, index) => {
      if (!descriptionMatchesRule(payeeKey, t.description || '')) return;
      if (matchByAmount && Math.abs(Math.abs(parseFloat(t.amount) || 0) - group.amount) >= 0.01) return;
      if (isIncome) {
        const desc = (t.description || '').toLowerCase();
        const isDebit = Number(t.amount) > 0 || desc.includes('debit');
        if (isDebit) return;
      }

      const row = { ...t, userCategory: group.category, category: group.category };
      if (group.category === 'recurring') {
        if (group.recurringType) row.recurringType = group.recurringType;
        if (group.recurringFrequency) row.recurringFrequency = group.recurringFrequency;
        if (group.recurringProperty) row.recurringProperty = group.recurringProperty;
        if (group.recurringIrsType) row.recurringIrsType = group.recurringIrsType;
      }
      if (group.category === 'new_bill') {
        if (group.newBillProperty) row.newBillProperty = group.newBillProperty;
        if (group.newBillIrsType) row.newBillIrsType = group.newBillIrsType;
      }
      if (group.category === 'rental_income') {
        if (group.propertyId) row.propertyId = group.propertyId;
        if (group.incomeType) row.incomeType = group.incomeType;
        row.isTaxable = true;
        row.isRentalIncome = true;
      }
      if (group.category === 'savings' && group.savingsAccount) row.savingsAccount = group.savingsAccount;
      if (group.category === 'child_expenses' && group.childIrsType) row.childIrsType = group.childIrsType;
      updatedList[index] = row;
      updatedBatch.push(row);
    });

    setAnalysisResults({ ...analysisResults, allTransactions: updatedList });
    setDuplicateTransactions(prev => prev.map((g, i) => i === groupIndex ? { ...g, applied: true } : g));
    setHasUnsavedChanges(true);

    if (group.transactions.length > 0) {
      autoSaveRule(group.transactions[0], group.category, {
        propertyId: group.propertyId,
        recurringProperty: group.recurringProperty,
        newBillProperty: group.newBillProperty,
        recurringIrsType: group.recurringIrsType,
        newBillIrsType: group.newBillIrsType,
        childIrsType: group.childIrsType
      });
    }
    
    toast.success(`Applied "${group.category}" to ${updatedBatch.length} line(s) from payee "${payeeKey}"`);
    
    if (onTransactionsCategorized) {
      onTransactionsCategorized({
        category: group.category,
        count: updatedBatch.length,
        transactions: updatedBatch,
        additionalData: {
          property: group.newBillProperty || group.recurringProperty || group.propertyId,
          irsType: group.newBillIrsType || group.recurringIrsType,
          incomeType: group.incomeType,
          savingsAccount: group.savingsAccount,
          childIrsType: group.childIrsType
        }
      });
    }
  };

  // Reset all bank analysis rules (start over)
  const resetAllBankRules = async () => {
    if (!window.confirm('Delete ALL bank analysis rules? This cannot be undone. You will start fresh.')) return;
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      const response = await axios.post('/api/expenses/reset-bank-rules', {}, { headers });
      if (response.data.success) {
        setLearningRules({});
        localStorage.removeItem('learningRules');
        toast.success('All statement rules deleted. Label transactions fresh — rules save by payee (amount can vary).');
        await loadServerRules(); // Refresh in case of cached state
      } else {
        toast.error('Failed to reset rules');
      }
    } catch (error) {
      console.error('Reset rules error:', error);
      toast.error('Failed to reset rules: ' + (error.response?.data?.message || error.message));
    }
  };

  // Save categorization rules to backend
  const saveCategorizationRules = async () => {
    try {
      const rules = [];
      
      // Collect rules from individual transactions
      if (analysisResults && analysisResults.allTransactions) {
        analysisResults.allTransactions.forEach(transaction => {
          if (transaction.userCategory) {
            const isZelle = isZelleDescription(transaction.description);
            rules.push({
              description: isZelle
                ? extractZellePayeeKey(transaction.description)
                : transaction.description,
              amount: transaction.amount,
              category: transaction.userCategory,
              type: isZelle ? 'pattern_match' : 'exact_match'
            });
          }
        });
      }
      
      // Collect rules from duplicate groups
      duplicateTransactions.forEach(group => {
        if (group.category) {
          rules.push({
            description: group.description,
            amount: group.amount,
            category: group.category,
            type: 'pattern_match'
          });
        }
      });
      
      const response = await axios.post('/api/expenses/save-categorization-rules', {
        rules: rules
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setHasUnsavedChanges(false);
        toast.success('Categorization rules saved successfully!');
      } else {
        toast.error('Failed to save rules: ' + response.data.message);
      }
    } catch (error) {
      console.error('Save rules error:', error);
      toast.error('Failed to save categorization rules: ' + error.message);
    }
  };

  // Batch save categorized transactions to expenses and income
  // Proceed with saving after duplicate review
  const proceedWithSaveAfterDuplicateReview = async (transactionsToSave, headers) => {
    setIsSaving(true);
    const saveToast = toast.loading(`Saving ${transactionsToSave.length} transactions...`);
    try {
      const response = await axios.post('/api/expenses/process-categorized-transactions', {
        transactions: transactionsToSave
      }, {
        headers,
        timeout: 300000 // 5 min for large batches (300+ transactions)
      });

      if (response.data.success) {
        toast.dismiss(saveToast);
        const { expensesCreated, incomeCreated, skipped } = response.data.data;
        toast.success(
          `✅ Saved ${expensesCreated} expense(s) and ${incomeCreated} income record(s). ${skipped} skipped.`,
          { duration: 5000 }
        );
        setHasUnsavedChanges(false);
        setQueuedTransactions([]); // Clear queue after successful save

        // Notify parent component to refresh expenses
        if (onTransactionsCategorized) {
          onTransactionsCategorized({
            category: 'batch_save',
            count: expensesCreated + incomeCreated,
            message: 'Transactions saved to expenses and income'
          });
        }
      } else {
        toast.dismiss(saveToast);
        toast.error('Failed to save transactions: ' + (response.data.message || response.data.error));
      }
    } catch (error) {
      toast.dismiss(saveToast);
      console.error('Save to expenses error:', error);
      toast.error('Failed to save transactions: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const saveToExpensesAndIncome = async () => {
    if (isSaving) return;
    const mainList = analysisResults?.allTransactions || [];
    const combined = [...queuedTransactions, ...mainList];
    if (combined.length === 0) {
      toast.error('No transactions to save');
      return;
    }

    try {
      // Prepare transactions for batch save (queued + remaining in main list)
      const transactionsToSave = combined
        .filter(tx => {
          // Only include categorized transactions that aren't ignored
          const category = tx.userCategory;
          return category && category !== 'ignore' && category !== 'savings';
        })
        .map(tx => ({
          date: tx.date,
          description: tx.description,
          amount: tx.amount,
          category: tx.userCategory,
          propertyId: tx.propertyId || tx.recurringProperty || tx.newBillProperty || null,
          recurringProperty: tx.recurringProperty || null,
          newBillProperty: tx.newBillProperty || null,
          incomeType: tx.incomeType || null,
          recurringType: tx.recurringType || null,
          recurringFrequency: tx.recurringFrequency || 'monthly',
          // IRS types for tax form mapping (property_taxes, mortgage_interest, insurance, etc.)
          recurringIrsType: tx.recurringIrsType || null,
          newBillIrsType: tx.newBillIrsType || null,
          childIrsType: tx.childIrsType || null
        }));

      if (transactionsToSave.length === 0) {
        toast.error('No categorized transactions to save');
        return;
      }

      // Dedupe within batch first (date + description + amount) - critical for full-year imports
      const seen = new Set();
      const dedupedTransactions = transactionsToSave.filter(tx => {
        const date = new Date(tx.date).toISOString().split('T')[0];
        const desc = (tx.description || '').toLowerCase().trim();
        const amt = Math.abs(parseFloat(tx.amount)).toFixed(2);
        const key = `${date}|${desc}|${amt}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const withinBatchRemoved = transactionsToSave.length - dedupedTransactions.length;
      if (withinBatchRemoved > 0) {
        toast.info(`Removed ${withinBatchRemoved} duplicate transaction(s) from this batch before import.`);
      }

      // Check for duplicates against database
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      
      try {
        const duplicateCheckRes = await axios.post('/api/expenses/check-duplicates', {
          transactions: dedupedTransactions
        }, { headers, timeout: 60000 }); // 1 min for duplicate check

        if (duplicateCheckRes.data.success && duplicateCheckRes.data.duplicates.length > 0) {
          // Show duplicate review modal instead of confirm dialog
          setDuplicateReviewModal({
            duplicates: duplicateCheckRes.data.duplicates,
            transactionsToSave: dedupedTransactions,
            headers: headers
          });
          return; // Don't proceed with save yet
        }
      } catch (dupError) {
        // If duplicate check fails, warn user but allow them to proceed
        console.warn('Duplicate check failed:', dupError);
        const proceed = window.confirm(
          '⚠️ Could not check for duplicates. The save may create duplicate entries.\n\n' +
          'Do you want to proceed anyway?'
        );
        if (!proceed) {
          return;
        }
      }

      // If we got here without duplicates, proceed with save
      await proceedWithSaveAfterDuplicateReview(dedupedTransactions, headers);
    } catch (error) {
      console.error('Save to expenses error:', error);
      toast.error('Failed to save transactions: ' + (error.response?.data?.message || error.message));
    }
  };

  // Delete transaction
  const deleteTransaction = async (transaction, reason = 'Not important') => {
    try {
      const response = await axios.post('/api/expenses/delete-transaction', {
        transaction: transaction,
        reason: reason
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        // Remove from current analysis results
        if (analysisResults && analysisResults.allTransactions) {
          const updatedTransactions = analysisResults.allTransactions.filter(t => 
            !(t.date === transaction.date && 
              t.description === transaction.description && 
              t.amount === transaction.amount)
          );
          
          setAnalysisResults({
            ...analysisResults,
            allTransactions: updatedTransactions
          });
        }
        
        // Add to deleted transactions list
        setDeletedTransactions(prev => [{
          ...transaction,
          deletedId: response.data.deletedId,
          undoUntil: response.data.undoUntil,
          reason: reason,
          deletedAt: new Date().toISOString()
        }, ...prev]);
        
        toast.success('Transaction deleted successfully!');
      } else {
        toast.error('Failed to delete transaction: ' + response.data.message);
      }
    } catch (error) {
      console.error('Delete transaction error:', error);
      toast.error('Failed to delete transaction: ' + error.message);
    }
  };

  // Undo transaction deletion
  const undoDeleteTransaction = async (deletedTransaction) => {
    try {
      const response = await axios.post('/api/expenses/undo-delete-transaction', {
        deletedTransactionId: deletedTransaction.deletedId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        // Remove from deleted transactions list
        setDeletedTransactions(prev => prev.filter(dt => dt.deletedId !== deletedTransaction.deletedId));
        
        // Add back to analysis results
        if (analysisResults && analysisResults.allTransactions) {
          setAnalysisResults({
            ...analysisResults,
            allTransactions: [...analysisResults.allTransactions, deletedTransaction]
          });
        }
        
        toast.success('Transaction deletion undone!');
      } else {
        toast.error('Failed to undo deletion: ' + response.data.message);
      }
    } catch (error) {
      console.error('Undo delete error:', error);
      toast.error('Failed to undo deletion: ' + error.message);
    }
  };

  // Load deleted transactions
  const loadDeletedTransactions = async () => {
    try {
      const response = await axios.get('/api/expenses/deleted-transactions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setDeletedTransactions(response.data.data);
      }
    } catch (error) {
      console.error('Load deleted transactions error:', error);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  // Load server rules on open (Zelle + General categorization rules)
  const loadServerRules = async () => {
    console.log('🔄 Loading server rules...');
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
      const map = {};

      try {
        const stmtResp = await axios.get('/api/expenses/statement-rules', { headers });
        if (stmtResp.data?.success && Array.isArray(stmtResp.data.data)) {
          stmtResp.data.data.forEach(r => {
            map[r.match_key] = {
              displayName: r.display_name || r.match_key,
              category: r.category,
              propertyId: r.property_id || null,
              propertyName: r.property_name || '',
              taxable: r.taxable === 1,
              disposition: r.disposition,
              budgetCategory: r.budget_category,
              avgAmount: r.avg_amount,
              lastAmount: r.last_amount,
              ruleType: 'statement'
            };
          });
        }
      } catch (e) {
        console.error('Error loading statement rules:', e);
      }

      // Legacy zelle + categorization (fallback)
      try {
        const zelleResp = await axios.get('/api/expenses/zelle-rules', { headers });
        if (zelleResp.data?.success && Array.isArray(zelleResp.data.data)) {
          zelleResp.data.data.forEach(r => {
            const key = extractMatchKey(r.payee_key);
            if (!map[key]) {
              map[key] = {
                displayName: r.display_name || key,
                category: r.category,
                propertyId: r.property_id || null,
                propertyName: r.property_name || '',
                taxable: true,
                ruleType: 'zelle'
              };
            }
          });
        }
      } catch (e) { /* ignore */ }

      if (Object.keys(map).length > 0) {
        setLearningRules(map);
        localStorage.setItem('learningRules', JSON.stringify(map));
        toast.success(`Loaded ${Object.keys(map).length} saved rules`);
      }
    } catch (e) {
      console.error('Error loading server rules:', e);
      toast.error('Failed to load rules');
    }
  };

  // Sync local browser rules to server persistence
  const syncLocalRulesToServer = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
      const local = JSON.parse(localStorage.getItem('learningRules') || '{}');
      const entries = Object.entries(local);
      if (entries.length === 0) return;
      await Promise.all(entries.map(([key, val]) => {
        return axios.post('/api/expenses/zelle-rules', {
          payee_key: key,
          display_name: val?.displayName || key,
          category: val?.category || 'unclassified',
          property_id: val?.propertyId || null,
          irs_type: val?.irsType || null
        }, { headers });
      }));
      // Reload from server to reflect persisted state
      await loadServerRules();
      toast.success('Local rules synced to server');
    } catch (e) {
      // silent
    }
  };

  // Save/update a single rule's category (e.g., mark as income vs not)
  const saveRuleCategory = async (payeeKey, rule, newCategory, propertyId = undefined, irsType = undefined) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
      const finalPropertyId = propertyId === undefined ? (rule?.propertyId ?? null) : propertyId;
      const finalIrsType = irsType === undefined ? (rule?.irsType ?? null) : irsType;
      const payload = {
        payee_key: payeeKey,
        display_name: rule?.displayName || payeeKey,
        category: newCategory,
        property_id: finalPropertyId,
        irs_type: finalIrsType || null
      };
      const resp = await axios.post('/api/expenses/zelle-rules', payload, { headers });
      if (resp.data?.success) {
        const updated = { ...learningRules };
        if (!updated[payeeKey]) updated[payeeKey] = {};
        updated[payeeKey] = { ...updated[payeeKey], category: newCategory, propertyId: finalPropertyId, irsType: finalIrsType };
        setLearningRules(updated);
        localStorage.setItem('learningRules', JSON.stringify(updated));
        toast.success('Rule updated');
      } else {
        toast.error('Failed to update rule');
      }
    } catch (e) {
      console.error('Save rule category error:', e);
      toast.error('Failed to update rule: ' + (e?.message || 'Unknown error'));
    }
  };

  const stats = getProgressStats();

  return (
    <div className="bank-analyzer-overlay">
      <div className="bank-analyzer-modal">
        <div className="bank-analyzer-header">
          <h2>🏦 Bank Statement Analysis</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {((analysisResults?.allTransactions?.length > 0) || queuedTransactions.length > 0) && (
              <button
                onClick={saveToExpensesAndIncome}
                disabled={isSaving || stats.importCount === 0}
                className="close-button"
                title="Import categorized transactions to Expenses & Income"
                style={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  background: stats.importCount > 0 ? '#10b981' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: stats.importCount > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: '500'
                }}
              >
                {isSaving ? '⏳ Importing...' : `💾 Import ${stats.importCount} transaction${stats.importCount !== 1 ? 's' : ''}`}
              </button>
            )}
            <button
              onClick={async () => {
                if (isMerging) return;
                setIsMerging(true);
                try {
                  const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
                  const res = await axios.post('/api/expenses/merge-recurring-duplicates', {}, { headers });
                  if (res.data?.success && res.data?.data) {
                    const { merged, deleted } = res.data.data;
                    if (deleted > 0) {
                      toast.success(`Merged ${merged} recurring groups, removed ${deleted} duplicate(s)`);
                      if (onTransactionsCategorized) onTransactionsCategorized({ category: 'merge', count: deleted });
                    } else {
                      toast.success('No recurring duplicates found');
                    }
                  } else {
                    toast.error(res.data?.error || 'Merge failed');
                  }
                } catch (e) {
                  toast.error('Merge failed: ' + (e.response?.data?.error || e.message));
                } finally {
                  setIsMerging(false);
                }
              }}
              disabled={isMerging}
              className="close-button"
              title="Merge duplicate recurring expenses (same subscription)"
              style={{
                fontSize: '12px',
                padding: '6px 10px',
                background: '#fef3c7',
                color: '#92400e',
                borderRadius: '6px',
                border: '1px solid #fcd34d',
              }}
            >
              {isMerging ? '⏳ Merging...' : '🔀 Merge Duplicates'}
            </button>
            <button
              onClick={() => setShowRules(!showRules)}
              className="close-button"
              title="View saved rules"
              style={{
                fontSize: '12px',
                padding: '6px 10px',
                background: '#eef2ff',
                color: '#3730a3',
                borderRadius: '6px',
                border: '1px solid #c7d2fe',
              }}
            >
              📚 Rules
            </button>
            {showRules && (
              <button
                onClick={loadServerRules}
                className="close-button"
                title="Refresh rules"
                style={{
                  fontSize: '12px',
                  padding: '6px 10px',
                  background: '#ecfeff',
                  color: '#155e75',
                  borderRadius: '6px',
                  border: '1px solid #a5f3fc',
                }}
              >
                ⟳ Refresh
              </button>
            )}
            <button onClick={onClose} className="close-button">×</button>
          </div>
        </div>

        {/* Sticky progress bar */}
        {analysisResults && (
          <div className="analyzer-progress-bar">
            <div className="progress-stat">
              <span className="progress-stat-value">{stats.total}</span>
              <span className="progress-stat-label">Total</span>
            </div>
            <div className="progress-stat progress-stat-success">
              <span className="progress-stat-value">{stats.importCount}</span>
              <span className="progress-stat-label">Ready</span>
            </div>
            <div className="progress-stat progress-stat-warning">
              <span className="progress-stat-value">{stats.uncategorized}</span>
              <span className="progress-stat-label">Need review</span>
            </div>
            <div className="progress-stat">
              <span className="progress-stat-value">{stats.queued}</span>
              <span className="progress-stat-label">Queued</span>
            </div>
            {stats.duplicateGroups > 0 && (
              <div className="progress-stat progress-stat-batch">
                <span className="progress-stat-value">{stats.duplicateGroups}</span>
                <span className="progress-stat-label">Batch groups</span>
              </div>
            )}
            {stats.uncategorized > 0 && (
              <button type="button" className="jump-next-btn" onClick={jumpToNextUncategorized}>
                ↓ Next uncategorized
              </button>
            )}
            <span className="rules-auto-save-badge">Rules auto-save ✓</span>
          </div>
        )}

        <div className="bank-analyzer-content">
          {/* Upload Section */}
          <div className="upload-section">
            <div 
              {...getRootProps()} 
              className={`dropzone ${isDragActive ? 'drag-active' : ''} ${isAnalyzing ? 'analyzing' : ''}`}
            >
              <input {...getInputProps()} />
              {isAnalyzing ? (
                <div className="analysis-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${analysisPhase === 'processing' ? 95 : analysisProgress}%` }}
                    ></div>
                  </div>
                  <p>{analysisPhase === 'processing' ? 'Parsing statements on server...' : `Uploading... ${analysisProgress}%`}</p>
                </div>
              ) : (
                <div className="upload-content">
                  <div className="upload-icon">📊</div>
                  <h3>Drop your USAA statements here</h3>
                  <p>Upload up to 24 PDF or CSV files (bank + credit card for full year)</p>
                  <p className="text-xs text-gray-500 mt-1">Tip: Rename files (e.g. USAA-Bank-2025-01.pdf) for easier tracking</p>
                  <p className="analysis-focus">
                    Auto-routes: income → taxable, expenses → deductions, rental → Schedule E
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Analysis Results */}
          {analysisResults && (
            <div className="analysis-sections">
              {/* Collapsible analytics summary */}
              <button
                type="button"
                className="analytics-toggle"
                onClick={() => setShowAnalytics(s => !s)}
              >
                {showAnalytics ? '▼' : '▶'} Analysis insights
                <span className="analytics-toggle-hint">
                  {autoCategorizedCount} auto-categorized · {analysisResults.allTransactions?.length || 0} transactions
                </span>
              </button>

              {showAnalytics && (
              <>
              <h3>📈 Analysis Results</h3>
              
              {/* Learning System Summary */}
              <div className="learning-summary">
                <div className="summary-card">
                  <div className="summary-icon">🤖</div>
                  <div className="summary-content">
                    <div className="summary-title">Auto-Categorized</div>
                    <div className="summary-value">{autoCategorizedCount}</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">❓</div>
                  <div className="summary-content">
                    <div className="summary-title">Need Review</div>
                    <div className="summary-value">{countNeedsReview(analysisResults?.allTransactions || [])}</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon">🧠</div>
                  <div className="summary-content">
                    <div className="summary-title">Learning Rules</div>
                    <div className="summary-value">{Object.keys(learningRules).length}</div>
                  </div>
                </div>
              </div>

              {/* Multi-Statement Processing Summary */}
              {analysisResults.mergeInfo && analysisResults.mergeInfo.totalStatements > 1 && (
                <div className="multi-statement-summary">
                  <h4>📊 Multi-Statement Processing</h4>
                  <div className="statement-breakdown">
                    <div className="breakdown-header">
                      <span>Processed {analysisResults.mergeInfo.totalStatements} statements</span>
                      {analysisResults.mergeInfo.duplicateTransactionsRemoved > 0 && (
                        <span className="duplicates-removed">
                          🔄 Removed {analysisResults.mergeInfo.duplicateTransactionsRemoved} duplicates
                        </span>
                      )}
                    </div>
                    {analysisResults.mergeInfo.statementPeriods && Object.keys(analysisResults.mergeInfo.statementPeriods).length > 0 && (
                      <div className="statement-periods">
                        {Object.entries(analysisResults.mergeInfo.statementPeriods)
                          .filter(([, p]) => p.fileCount > 1)
                          .map(([fp, p]) => (
                            <div key={fp} className="period-note">
                              📅 {p.closingDate || 'Unknown period'}: {p.fileCount} files merged ({p.files.join(', ')})
                            </div>
                          ))}
                      </div>
                    )}
                    <div className="statement-details">
                      {Object.entries(analysisResults.mergeInfo.statementBreakdown).map(([fileName, details]) => (
                        <div key={fileName} className="statement-detail">
                          <div className="statement-name">{fileName}</div>
                          <div className="statement-info">
                            <span className="file-type">{details.fileType}</span>
                            <span className="transaction-count">{details.transactionCount} transactions</span>
                            {details.dateRange && (
                              <span className="date-range">
                                {details.dateRange.start} to {details.dateRange.end}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {analysisResults.mergeInfo.duplicationDetails && (
                      <div className="duplication-details">
                        <h5>Deduplication Details:</h5>
                        <ul>
                          {analysisResults.mergeInfo.duplicationDetails.exactDuplicates > 0 && (
                            <li>Exact duplicates: {analysisResults.mergeInfo.duplicationDetails.exactDuplicates}</li>
                          )}
                          {analysisResults.mergeInfo.duplicationDetails.similarTransactions > 0 && (
                            <li>Similar transactions: {analysisResults.mergeInfo.duplicationDetails.similarTransactions}</li>
                          )}
                          {analysisResults.mergeInfo.duplicationDetails.monthBasedMerging > 0 && (
                            <li>Month-based merging: {analysisResults.mergeInfo.duplicationDetails.monthBasedMerging}</li>
                          )}
                        </ul>
                      </div>
                    )}
                    {analysisResults.mergeInfo.fileErrors?.length > 0 && (
                      <div className="file-errors">
                        <h5>⚠️ Files with errors:</h5>
                        <ul>
                          {analysisResults.mergeInfo.fileErrors.map(({ fileName, error }, i) => (
                            <li key={i}><strong>{fileName}</strong>: {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Rental Income Analysis */}
              <div className="analysis-section">
                <h4>💰 Rental Income Analysis</h4>
                <div className="income-grid">
                  <div className="income-card">
                    <span className="income-label">Consistent Monthly Income:</span>
                    <span className="income-value">${analysisResults.rentalIncome?.consistent || 0}</span>
                  </div>
                  <div className="income-card">
                    <span className="income-label">New This Month:</span>
                    <span className="income-value">${analysisResults.rentalIncome?.newThisMonth || 0}</span>
                  </div>
                  <div className="income-card">
                    <span className="income-label">Total Monthly Income:</span>
                    <span className="income-value">${analysisResults.rentalIncome?.total || 0}</span>
                  </div>
                </div>
                {analysisResults.rentalIncome?.transactions && analysisResults.rentalIncome.transactions.length > 0 && (
                  <div className="transaction-list">
                    <h5>Rental Income Transactions:</h5>
                    {analysisResults.rentalIncome.transactions.map((transaction, index) => (
                      <div key={index} className="transaction-item">
                        <span className="transaction-date">{transaction.date}</span>
                        <span className="transaction-description">{transaction.description}</span>
                        <span className="transaction-amount">${transaction.amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Costa Rica Transactions */}
              <div className="analysis-section">
                <h4>🇨🇷 Costa Rica Transactions</h4>
                <div className="costa-rica-stats">
                  <div className="stat-card">
                    <span className="stat-label">Total Costa Rica Expenses:</span>
                    <span className="stat-value">${analysisResults.costaRica?.total || 0}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Monthly Average:</span>
                    <span className="stat-value">${analysisResults.costaRica?.monthlyAverage || 0}</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-label">Transaction Count:</span>
                    <span className="stat-value">{analysisResults.costaRica?.count || 0}</span>
                  </div>
                </div>
                {analysisResults.costaRica?.transactions && analysisResults.costaRica.transactions.length > 0 && (
                  <div className="transaction-list">
                    <h5>Costa Rica Transactions:</h5>
                    {analysisResults.costaRica.transactions.slice(0, 10).map((transaction, index) => (
                      <div key={index} className="transaction-item">
                        <span className="transaction-date">{transaction.date}</span>
                        <span className="transaction-description">{transaction.description}</span>
                        <span className="transaction-amount">${transaction.amount}</span>
                      </div>
                    ))}
                    {analysisResults.costaRica.transactions.length > 10 && (
                      <p className="more-transactions">... and {analysisResults.costaRica.transactions.length - 10} more transactions</p>
                    )}
                  </div>
                )}
              </div>

              {/* Recurring Expenses */}
              <div className="analysis-section">
                <h4>🔄 Recurring Expenses</h4>
                <div className="recurring-expenses">
                  {analysisResults.recurringExpenses && analysisResults.recurringExpenses.length > 0 ? (
                    analysisResults.recurringExpenses.map((expense, index) => (
                      <div key={index} className="recurring-item">
                        <div className="recurring-header">
                          <div className="recurring-info">
                            <span className="recurring-description">{expense.description}</span>
                            <span className="recurring-amount">${expense.amount}</span>
                          </div>
                          <div className="recurring-badges">
                            {expense.isConsistent && <span className="badge consistent">Consistent</span>}
                            <span className="badge frequency">{expense.frequency}</span>
                          </div>
                        </div>
                        <div className="recurring-details">
                          <div className="detail-row">
                            <span className="detail-label">Frequency:</span>
                            <span className="detail-value">{expense.frequency}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Months Active:</span>
                            <span className="detail-value">{expense.months}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Total Spent:</span>
                            <span className="detail-value">${expense.totalSpent}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Category:</span>
                            <span className="detail-value">{expense.category}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Date Range:</span>
                            <span className="detail-value">{expense.firstSeen} to {expense.lastSeen}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">No recurring expenses identified across multiple statements</p>
                  )}
                </div>
              </div>

              {/* New Ongoing Bills */}
              <div className="analysis-section">
                <h4>🆕 New Ongoing Bills</h4>
                <div className="new-bills">
                  {analysisResults.newBills && analysisResults.newBills.length > 0 ? (
                    analysisResults.newBills.map((bill, index) => (
                      <div key={index} className="new-bill-item">
                        <div className="bill-info">
                          <span className="bill-description">{bill.description}</span>
                          <span className="bill-amount">${bill.amount}</span>
                        </div>
                        <div className="bill-details">
                          First seen: {bill.firstSeen} • Category: {bill.category}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">No new ongoing bills identified</p>
                  )}
                </div>
              </div>

              {/* Expense Categories */}
              <div className="analysis-section">
                <h4>📊 Expense Categories</h4>
                <div className="category-breakdown">
                  {analysisResults.categories && Object.keys(analysisResults.categories).length > 0 ? (
                    Object.entries(analysisResults.categories).map(([category, data]) => (
                      <div key={category} className="category-item">
                        <div className="category-info">
                          <span className="category-name">{category}</span>
                          <span className="category-amount">${data.total || 0}</span>
                        </div>
                        <div className="category-details">
                          {data.count || 0} transactions • ${data.average || 0} average
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">No expense categories identified</p>
                  )}
                </div>
              </div>
              </>
              )}

              {/* Interactive Transaction Categorization */}
              <div className="analysis-section categorization-workspace">
                <h4>📋 Categorize Transactions</h4>
                <p className="categorization-help">
                  Label each line with <strong>Taxable?</strong>, property, and category — parser suggestions are hints only until you confirm.
                  Use <strong>Need review</strong> to focus on unlabeled lines; switch to <strong>All</strong> to see everything.
                </p>

                {/* Mortgage payments — handle at top via calculator profiles */}
                {mortgageMatches.length > 0 && (
                  <div
                    className="mortgage-statement-section"
                    style={{
                      marginBottom: 20,
                      border: '1px solid #93c5fd',
                      borderRadius: 10,
                      background: '#eff6ff',
                      overflow: 'hidden'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setMortgageSectionCollapsed(c => !c)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 700,
                        color: '#1e3a8a',
                        fontSize: 15
                      }}
                    >
                      <span>🏠 Mortgage payments ({mortgageMatches.length}) — link to calculator, don’t deduct full ACH</span>
                      <span>{mortgageSectionCollapsed ? '▶' : '▼'}</span>
                    </button>
                    {!mortgageSectionCollapsed && (
                      <div style={{ padding: '0 16px 16px' }}>
                        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#1e40af' }}>
                          Only <strong>interest</strong> (and escrow taxes/insurance) is deductible.
                          Set up or sync estimates in{' '}
                          <a href="/mortgage-calculator" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>
                            Mortgage Calculator
                          </a>
                          . Linking marks the bank payment for cash flow without treating the whole amount as Schedule E interest.
                        </p>
                        {mortgageProfiles.length === 0 ? (
                          <p style={{ fontSize: 13, color: '#6b7280' }}>
                            No mortgage profiles yet.{' '}
                            <a href="/mortgage-calculator" target="_blank" rel="noreferrer">Create one</a>
                            {' '}for each rental, then re-analyze or link manually below.
                          </p>
                        ) : (
                          <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 13 }}>Link all detected →</span>
                            <select
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value) linkAllMortgageMatchesToProfile(e.target.value);
                                e.target.value = '';
                              }}
                              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #93c5fd' }}
                            >
                              <option value="">Choose property mortgage…</option>
                              {mortgageProfiles.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.property_name}{p.review_due ? ' (review due)' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {mortgageMatches.map((m, i) => {
                          const tx = analysisResults?.allTransactions?.[m.index];
                          const linked = tx?.userCategory === 'mortgage_payment';
                          return (
                            <div
                              key={`${m.index}-${i}`}
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 10,
                                alignItems: 'center',
                                padding: '10px 0',
                                borderTop: '1px solid #bfdbfe',
                                fontSize: 13
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ fontWeight: 600 }}>{m.description || tx?.description}</div>
                                <div style={{ color: '#64748b' }}>
                                  {m.date || tx?.date} · ${Math.abs(m.amount ?? tx?.amount ?? 0).toFixed(2)}
                                  {m.estimated_split && (
                                    <span style={{ marginLeft: 8, color: '#166534' }}>
                                      est. interest ${Number(m.estimated_split.interest || 0).toFixed(2)}
                                      {m.estimated_split.escrow > 0 ? ` · escrow $${Number(m.estimated_split.escrow).toFixed(2)}` : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {linked ? (
                                <span style={{ color: '#166534', fontWeight: 600 }}>
                                  ✓ Linked{m.property_name ? `: ${m.property_name}` : ''}
                                </span>
                              ) : mortgageProfiles.length > 0 ? (
                                <select
                                  defaultValue=""
                                  onChange={(e) => {
                                    if (e.target.value) linkMortgageMatch(i, e.target.value);
                                  }}
                                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #93c5fd' }}
                                >
                                  <option value="">Link to…</option>
                                  {mortgageProfiles.map(p => (
                                    <option key={p.id} value={p.id}>{p.property_name}</option>
                                  ))}
                                </select>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Batch duplicate groups — categorize recurring merchants once */}
                {duplicateTransactions.filter(g => !g.applied).length > 0 && (
                  <div className="duplicate-group">
                    <h5>⚡ Batch Groups — same payee + same amount (e.g. monthly rent)</h5>
                    {duplicateTransactions.map((group, groupIndex) => {
                      if (group.applied) return null;
                      const displayDesc = group.payeeKey || getMeaningfulWords(group.description, 6).join(' ') || group.description;
                      return (
                        <div key={groupIndex} className="duplicate-group-item">
                          <div className="group-header">
                            <span className="group-description">{displayDesc}</span>
                            <span className="group-count">{group.transactions.length}×</span>
                            <span className="group-amount">${group.amount.toFixed(2)}</span>
                          </div>
                          <div className="group-categorization">
                            <select
                              value={group.category || ''}
                              onChange={(e) => handleGroupCategorization(groupIndex, e.target.value)}
                              className="category-dropdown"
                            >
                              <option value="">Select category...</option>
                              <option value="recurring">🔄 Recurring Expense</option>
                              <option value="new_bill">📋 New Bill</option>
                              <option value="rental_income">🏠 Rental Income</option>
                              <option value="costa_rica_expense">🇨🇷 Costa Rica</option>
                              <option value="travel">✈️ Travel</option>
                              <option value="utilities">💡 Utilities</option>
                              <option value="other">❓ Other</option>
                            </select>
                            {group.category === 'rental_income' && propertiesLoaded && (
                              <select
                                value={group.propertyId || ''}
                                onChange={(e) => {
                                  const updated = [...duplicateTransactions];
                                  updated[groupIndex].propertyId = e.target.value ? parseInt(e.target.value, 10) : null;
                                  setDuplicateTransactions(updated);
                                }}
                                className="property-dropdown"
                              >
                                <option value="">Property...</option>
                                {properties.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            )}
                            {group.category === 'recurring' && propertiesLoaded && (
                              <select
                                value={group.recurringProperty || ''}
                                onChange={(e) => handleGroupRecurringProperty(groupIndex, e.target.value ? parseInt(e.target.value, 10) : null)}
                                className="property-dropdown"
                              >
                                <option value="">Property...</option>
                                {properties.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            )}
                            <button
                              type="button"
                              className="apply-group-btn"
                              onClick={() => applyGroupCategorization(groupIndex)}
                              disabled={!group.category}
                            >
                              Apply to payee ({group.transactions.length} lines, ${group.amount.toFixed(2)} each)
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Queued for import */}
                {queuedTransactions.length > 0 && (
                  <div className="queued-section" style={{ marginBottom: '20px', border: '1px solid #10b981', borderRadius: '8px', overflow: 'hidden', background: '#f0fdf4' }}>
                    <button
                      type="button"
                      onClick={() => setShowQueuedSection(s => !s)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#065f46'
                      }}
                    >
                      <span>✅ Queued for import ({queuedTransactions.length})</span>
                      <span style={{ fontSize: '18px' }}>{showQueuedSection ? '▼' : '▶'}</span>
                    </button>
                    {showQueuedSection && (
                      <div style={{ padding: '0 16px 16px', maxHeight: '200px', overflowY: 'auto' }}>
                        {queuedTransactions.map((t, i) => (
                          <div key={i} style={{ padding: '8px 0', borderBottom: i < queuedTransactions.length - 1 ? '1px solid #bbf7d0' : 'none', fontSize: '13px', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                            <span style={{ flex: 1, wordBreak: 'break-word' }}>{t.description}</span>
                            <span style={{ whiteSpace: 'nowrap', color: '#047857', fontWeight: '500' }}>${Math.abs(t.amount).toFixed(2)}</span>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setAnalysisResults(prev => prev ? { ...prev, allTransactions: [...(prev.allTransactions || []), ...queuedTransactions] } : prev);
                            setQueuedTransactions([]);
                            setHasUnsavedChanges(true);
                            toast.success('Restored all queued transactions to the list');
                          }}
                          style={{
                            marginTop: '12px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            background: '#fef3c7',
                            border: '1px solid #fcd34d',
                            borderRadius: '6px',
                            color: '#92400e',
                            cursor: 'pointer'
                          }}
                        >
                          ↩ Restore all to list
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Individual Transactions */}
                <div className="individual-transactions">
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <h5 style={{ margin: 0 }}>📄 Individual Transactions ({analysisResults.allTransactions?.length || 0} total)</h5>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="Search description or amount..."
                        value={transactionSearchText}
                        onChange={(e) => {
                          setTransactionSearchText(e.target.value);
                          if (e.target.value.trim()) setTransactionFilter('search');
                        }}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '13px',
                          minWidth: '180px'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => { setTransactionFilter('uncategorized'); setTransactionSearchText(''); }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: transactionFilter === 'uncategorized' ? '#3b82f6' : '#f3f4f6',
                            color: transactionFilter === 'uncategorized' ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontWeight: transactionFilter === 'uncategorized' ? 600 : 400
                          }}
                        >
                          ⏳ Need review ({countNeedsReview(analysisResults?.allTransactions)})
                        </button>
                        <button
                          type="button"
                          onClick={() => { setTransactionFilter('all'); setTransactionSearchText(''); }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: transactionFilter === 'all' ? '#3b82f6' : '#f3f4f6',
                            color: transactionFilter === 'all' ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontWeight: transactionFilter === 'all' ? 600 : 400
                          }}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => { setTransactionFilter('w2'); setTransactionSearchText(''); }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: transactionFilter === 'w2' ? '#3b82f6' : '#f3f4f6',
                            color: transactionFilter === 'w2' ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontWeight: transactionFilter === 'w2' ? 600 : 400
                          }}
                        >
                          📄 W-2 Income ({analysisResults?.allTransactions?.filter(t => (t.userCategory || t.category) === 'w2_income' || isLikelyW2Income(t)).length || 0})
                        </button>
                        <button
                          type="button"
                          onClick={() => { setTransactionFilter('atm'); setTransactionSearchText(''); }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            background: transactionFilter === 'atm' ? '#3b82f6' : '#f3f4f6',
                            color: transactionFilter === 'atm' ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontWeight: transactionFilter === 'atm' ? 600 : 400
                          }}
                        >
                          🏧 ATM ({analysisResults?.allTransactions?.filter(t => isAtmWithdrawal(t.description)).length || 0})
                        </button>
                      </div>
                    </div>
                  </div>
                  {analysisResults.allTransactions && analysisResults.allTransactions.length > 0 ? (
                    (() => {
                      const filtered = getFilteredTransactions();
                      if (filtered.length === 0) {
                        return (
                          <p style={{ padding: '16px', color: '#6b7280', fontSize: '14px' }}                          >
                            {transactionFilter === 'atm' ? 'No ATM withdrawals found.' :
                             transactionFilter === 'w2' ? 'No W-2 income transactions found.' :
                             transactionFilter === 'uncategorized' ? 'All transactions are labeled.' :
                             transactionSearchText ? `No transactions match "${transactionSearchText}".` : 'No transactions.'}
                          </p>
                        );
                      }
                      return filtered.map(({ t: transaction, originalIndex: index }) => (
                      <div key={index} id={`txn-row-${index}`} className="transaction-item interactive">
                        <div className="transaction-info">
                          <div className="transaction-date">
                            {transaction.date}
                            {transaction.sourceAccount === 'creditCard' && (
                              <span className="source-badge source-credit-card" title="From credit card statement">💳 CC</span>
                            )}
                            {transaction.sourceAccount === 'bank' && (
                              <span className="source-badge source-bank" title="From bank statement">🏦 Bank</span>
                            )}
                          </div>
                          <div className="transaction-description">
                            {transaction.description}
                            {transaction.autoCategorized && (
                              <span className="auto-categorized-badge" title={`Auto-categorized with ${transaction.confidence}% confidence`}>
                                🤖 Auto
                              </span>
                            )}
                          </div>
                          <div className="transaction-amount">${transaction.amount.toFixed(2)}</div>
                        </div>
                        <div className="transaction-categorization statement-label-flow">
                          {transaction.userCategory ? (
                            <div className="labeled-summary" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                              <span className="user-category" style={{ fontWeight: 600, color: '#166534' }}>
                                ✓ {transaction.isTaxable === false ? 'Personal' : 'Tax'}: {transaction.userCategory}
                              </span>
                              {transaction.propertyId && (
                                <span className="property-badge" style={{ fontSize: 12, color: '#1d4ed8' }}>
                                  {properties.find(p => p.id === transaction.propertyId)?.name || 'Property'}
                                </span>
                              )}
                              {learningRules[extractMatchKey(transaction.description)] && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>Rule saved</span>
                              )}
                            </div>
                          ) : (
                            <>
                              {transaction.category && (
                                <span
                                  className="suggested-category-badge"
                                  title="Parser guess — confirm with Taxable? and Category below"
                                  style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 4 }}
                                >
                                  Suggested: {transaction.category}
                                </span>
                              )}
                              <select
                                className="category-dropdown"
                                defaultValue=""
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (!v) return;
                                  const updated = [...analysisResults.allTransactions];
                                  updated[index] = { ...transaction, _labelTaxable: v };
                                  setAnalysisResults({ ...analysisResults, allTransactions: updated });
                                }}
                                style={{ minWidth: 120 }}
                              >
                                <option value="">Taxable?</option>
                                <option value="yes">Yes — tax</option>
                                <option value="no">No — budget</option>
                                <option value="ignore">Transfer / ignore</option>
                              </select>
                              <select
                                className="category-dropdown"
                                defaultValue={transaction.propertyId || ''}
                                onChange={(e) => {
                                  const pid = e.target.value ? parseInt(e.target.value, 10) : null;
                                  const updated = [...analysisResults.allTransactions];
                                  updated[index] = { ...transaction, propertyId: pid, _labelProperty: pid };
                                  setAnalysisResults({ ...analysisResults, allTransactions: updated });
                                }}
                                style={{ minWidth: 140 }}
                              >
                                <option value="">House / property</option>
                                {properties.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}{p.type === 'primary' ? ' (home)' : ''}</option>
                                ))}
                              </select>
                              <select
                                className="category-dropdown"
                                key={`label-cat-${index}-${transaction.userCategory || 'new'}`}
                                defaultValue=""
                                onChange={async (e) => {
                                  const cat = e.target.value;
                                  if (!cat) return;
                                  const isBudget = BUDGET_CATEGORIES.some(c => c.value === cat);
                                  const taxableChoice = transaction._labelTaxable || (isBudget ? 'no' : 'yes');
                                  const propId = transaction._labelProperty ?? transaction.propertyId ?? null;
                                  await handleStatementLabel(index, taxableChoice, cat, propId);
                                }}
                                style={{ minWidth: 160 }}
                              >
                                <option value="">Category</option>
                                <optgroup label="Tax (Schedule E)">
                                  {SCHEDULE_E_CATEGORIES.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="Personal budget">
                                  {BUDGET_CATEGORIES.map(c => (
                                    <option key={`b-${c.value}`} value={c.value}>{c.label}</option>
                                  ))}
                                </optgroup>
                              </select>
                              <span style={{ fontSize: 11, color: '#6b7280' }}>Saves rule by payee</span>
                            </>
                          )}
                          {transaction.userCategory && (
                            <button
                              type="button"
                              className="apply-similar-btn"
                              onClick={() => handleTransactionRecategorizeSimilar(index)}
                              title="Apply this category to all transactions with the same merchant"
                            >
                              📋 Apply to similar
                            </button>
                          )}
                          
                          {/* Income Button */}
                          <button
                            onClick={() => {
                              const key = ruleKeyForDescription(transaction.description || '');
                              const current = learningRules[key] || { displayName: key };
                              const updated = { ...learningRules, [key]: { ...current, category: 'income' } };
                              setLearningRules(updated);
                              localStorage.setItem('learningRules', JSON.stringify(updated));
                              saveRuleCategory(key, updated[key], 'income');
                              
                              // Update the transaction
                              const updatedTransactions = [...analysisResults.allTransactions];
                              updatedTransactions[index] = { ...transaction, userCategory: 'income', category: 'income' };
                              setAnalysisResults({ ...analysisResults, allTransactions: updatedTransactions });
                              toast.success('Marked as income');
                            }}
                            style={{ 
                              fontSize: '12px', 
                              padding: '4px 8px', 
                              marginTop: '8px',
                              background: '#fef3c7', 
                              border: '1px solid #fcd34d', 
                              borderRadius: '6px', 
                              color: '#92400e',
                              cursor: 'pointer'
                            }}
                          >
                            💰 Income
                          </button>
                          
                          {/* Recurring Expense Sub-Category for Individual Transactions - hide when in Apply to Similar modal */}
                          {(transaction.userCategory === 'recurring' || transaction.category === 'recurring') && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <div className="recurring-fields">
                              <select 
                                value={transaction.recurringType || ''} 
                                onChange={(e) => handleTransactionRecurringType(index, e.target.value)}
                                className="recurring-type-dropdown"
                              >
                                <option value="">Select Recurring Type</option>
                                <option value="recurring_mortgage">🏠 Mortgage Payment</option>
                                <option value="recurring_loan">💰 Loan Payment</option>
                                <option value="recurring_gas">⛽ Gas/Utility Bill</option>
                                <option value="recurring_insurance">🛡️ Insurance</option>
                                <option value="recurring_phone">📱 Phone/Internet</option>
                                <option value="recurring_subscription">📺 Subscription Service</option>
                                <option value="recurring_credit_card">💳 Credit Card Payment</option>
                                <option value="recurring_property_taxes">🏛️ Property Taxes</option>
                                <option value="recurring_other">🔄 Other Recurring</option>
                              </select>
                              <select 
                                value={transaction.recurringFrequency || 'monthly'} 
                                onChange={(e) => handleTransactionRecurringFrequency(index, e.target.value)}
                                className="recurring-frequency-dropdown"
                                title="How often this expense repeats"
                              >
                                <option value="monthly">📅 Monthly</option>
                                <option value="quarterly">📆 Quarterly</option>
                                <option value="semi_annually">📈 Semi-Annually</option>
                                <option value="yearly">🗓️ Annually</option>
                              </select>
                              
                              {/* Property Selection for Individual Recurring Expenses */}
                              {propertiesLoaded && (
                                <select 
                                  value={transaction.recurringProperty || ''} 
                                  onChange={(e) => handleTransactionRecurringProperty(index, e.target.value)}
                                  className="property-dropdown"
                                >
                                  <option value="">Select Property (Required)</option>
                                  {properties && properties.length > 0 ? properties.map((property) => (
                                    <option key={property.id} value={property.id}>
                                      {property.name} - {property.address}
                                    </option>
                                  )) : (
                                    <option value="" disabled>No properties found</option>
                                  )}
                                </select>
                              )}
                              
                              {/* IRS Type for Individual Recurring Expenses */}
                              <select 
                                value={transaction.recurringIrsType || ''} 
                                onChange={(e) => handleTransactionRecurringIrsType(index, e.target.value)}
                                className="irs-type-dropdown"
                              >
                                <option value="">Select IRS Deduction Type</option>
                                {Object.entries(irsDeductionTypes).map(([key, irsType]) => (
                                  <option key={key} value={key}>
                                    {irsType.name} ({irsType.code})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {/* New Bill Fields for Individual Transactions - hide when in Apply to Similar modal (set there instead) */}
                          {(transaction.userCategory === 'new_bill' || transaction.category === 'new_bill') && !propertiesLoaded && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <div className="new-bill-fields">
                              <div className="loading-message">Loading properties...</div>
                            </div>
                          )}
                          {(transaction.userCategory === 'new_bill' || transaction.category === 'new_bill') && propertiesLoaded && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <div className="new-bill-fields">
                              <select 
                                value={newBillProperties[index] ?? transaction.newBillProperty ?? ''} 
                                onChange={(e) => handleNewBillProperty(index, e.target.value)}
                                className="property-dropdown"
                              >
                                <option value="">Select Property (Required)</option>
                                {properties && properties.length > 0 ? properties.map((property) => (
                                  <option key={property.id} value={property.id}>
                                    {property.name} - {property.address}
                                  </option>
                                )) : (
                                  <option value="" disabled>No properties found</option>
                                )}
                              </select>
                              
                              <select 
                                value={newBillIrsTypes[index] ?? transaction.newBillIrsType ?? ''} 
                                onChange={(e) => handleNewBillIrsType(index, e.target.value)}
                                className="irs-type-dropdown"
                              >
                                <option value="">Select IRS Deduction Type</option>
                                {Object.entries(irsDeductionTypes).map(([key, irsType]) => (
                                  <option key={key} value={key}>
                                    {irsType.name} ({irsType.code})
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {/* Property Selection for Rental Income - only after user confirmed label */}
                          {transaction.userCategory === 'rental_income' && propertiesLoaded && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <select 
                              value={transaction.propertyId || ''} 
                              onChange={(e) => {
                                const propertyId = e.target.value ? parseInt(e.target.value) : null;
                                const updatedTransactions = [...analysisResults.allTransactions];
                                updatedTransactions[index] = { ...transaction, propertyId };
                                setAnalysisResults({ ...analysisResults, allTransactions: updatedTransactions });
                                setHasUnsavedChanges(true);
                                autoSaveRule(transaction, 'rental_income', { propertyId });
                              }}
                              className="property-dropdown"
                            >
                              <option value="">Select Property (Required)</option>
                              {properties && properties.length > 0 ? properties.map((property) => (
                                <option key={property.id} value={property.id}>
                                  {property.name} {property.type === 'primary' ? '(Primary - not taxable)' : ''} - {property.address}
                                </option>
                              )) : (
                                <option value="" disabled>No properties found</option>
                              )}
                            </select>
                          )}
                          
                          {/* Income Type for Individual Transactions - hide when in Apply to Similar modal */}
                          {transaction.userCategory === 'rental_income' && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <select 
                              value={incomeTypes[index] || ''} 
                              onChange={(e) => handleIncomeType(index, e.target.value)}
                              className="income-type-dropdown"
                            >
                              <option value="">Select Income Type</option>
                              {Object.entries(incomeTypeOptions).map(([key, incomeType]) => (
                                <option key={key} value={key}>
                                  {incomeType.icon} {incomeType.name}
                                </option>
                              ))}
                            </select>
                          )}
                          
                          {/* Savings Account Selection for Individual Transactions - hide when in Apply to Similar modal */}
                          {(transaction.userCategory === 'savings' || transaction.category === 'savings') && accountsLoaded && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <select 
                              value={transaction.savingsAccount || ''} 
                              onChange={(e) => handleTransactionSavingsAccount(index, e.target.value)}
                              className="savings-account-dropdown"
                            >
                              <option value="">Select Savings Account (Required)</option>
                              {accounts && accounts.length > 0 ? accounts.map((account) => (
                                <option key={account.id} value={account.id}>
                                  {account.name} - {account.account_type} ({account.bank_name})
                                </option>
                              )) : (
                                <option value="" disabled>No accounts found</option>
                              )}
                            </select>
                          )}
                          
                          {/* Child Expenses Fields for Individual Transactions - hide when in Apply to Similar modal */}
                          {(transaction.userCategory === 'child_expenses' || transaction.category === 'child_expenses') && !(applySimilarModal && applySimilarModal.indices?.includes(index)) && (
                            <div className="child-expenses-fields">
                              <select 
                                value={transaction.childIrsType || ''} 
                                onChange={(e) => handleTransactionChildIrsType(index, e.target.value)}
                                className="irs-type-dropdown"
                              >
                                <option value="">Select IRS Deduction Type</option>
                                <option value="child_care">Child Care Expenses (2441)</option>
                                <option value="child_education">Child Education - Private School (529/ESA)</option>
                                <option value="dependent_care_fsa">Dependent Care FSA</option>
                                <option value="education_expenses">Education Expenses (1098-T)</option>
                                <option value="student_loan_interest">Student Loan Interest (1098-E)</option>
                              </select>
                            </div>
                          )}
                          
                          <div className="current-category">
                            {transaction.userCategory && (
                              <span className="user-category">✓ {transaction.userCategory}</span>
                            )}
                            {!transaction.userCategory && transaction.category && (
                              <span className="auto-category">🤖 {transaction.category}</span>
                            )}
                          </div>
                          <button 
                            onClick={() => {
                              if (window.confirm(`Delete this transaction?\n\n${transaction.description}\n$${transaction.amount.toFixed(2)}`)) {
                                deleteTransaction(transaction, 'Not important');
                              }
                            }}
                            className="delete-transaction-btn"
                            title="Delete transaction (can be undone for 60 days)"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ));
                    })()
                  ) : (
                    <p>No transactions found.</p>
                  )}
                </div>

                {/* Rules auto-save — no manual save needed */}
                <p className="auto-save-note">Rules save automatically when you categorize.</p>

                {/* Deleted Transactions Management */}
                <div className="deleted-transactions-section">
                  <div className="deleted-transactions-header">
                    <button 
                      onClick={() => {
                        setShowDeletedTransactions(!showDeletedTransactions);
                        if (!showDeletedTransactions) {
                          loadDeletedTransactions();
                        }
                      }}
                      className="show-deleted-btn"
                    >
                      {showDeletedTransactions ? '🔼 Hide' : '🔽 Show'} Deleted Transactions ({deletedTransactions.length})
                    </button>
                    <span className="deleted-info">
                      💡 Deleted transactions can be undone for 60 days
                    </span>
                  </div>

                  {showDeletedTransactions && (
                    <div className="deleted-transactions-list">
                      {deletedTransactions.length > 0 ? (
                        deletedTransactions.map((deletedTransaction, index) => {
                          const undoUntil = new Date(deletedTransaction.undoUntil || deletedTransaction.permanent_delete_date);
                          const now = new Date();
                          const canUndo = now < undoUntil;
                          const daysLeft = Math.ceil((undoUntil - now) / (1000 * 60 * 60 * 24));

                          return (
                            <div key={index} className="deleted-transaction-item">
                              <div className="deleted-transaction-info">
                                <div className="deleted-date">{deletedTransaction.date}</div>
                                <div className="deleted-description">{deletedTransaction.description}</div>
                                <div className="deleted-amount">${deletedTransaction.amount?.toFixed(2) || '0.00'}</div>
                                <div className="deleted-reason">Reason: {deletedTransaction.reason}</div>
                              </div>
                              <div className="deleted-transaction-actions">
                                {canUndo ? (
                                  <>
                                    <button 
                                      onClick={() => undoDeleteTransaction(deletedTransaction)}
                                      className="undo-delete-btn"
                                    >
                                      ↩️ Undo
                                    </button>
                                    <span className="undo-info">
                                      {daysLeft} days left
                                    </span>
                                  </>
                                ) : (
                                  <span className="expired-info">
                                    ⏰ Expired
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="no-deleted">No deleted transactions</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer actions */}
              <div className="analysis-actions">
                <button 
                  onClick={() => { setAnalysisResults(null); setDuplicateTransactions([]); setQueuedTransactions([]); }}
                  className="reset-button"
                >
                  Upload New Statements
                </button>
              </div>

            </div>
          )}
        </div>

        {/* Rules Panel */}
        {showRules && (() => {
          console.log('🎨 Rendering Rules Panel - showRules is true');
          console.log('📊 Current learningRules state:', learningRules);
          console.log('📊 Number of rules in learningRules:', Object.keys(learningRules).length);
          const ruleCount = Object.keys(learningRules).length;
          return (
          <div style={{
            borderTop: '1px solid #e5e7eb',
            padding: '12px 16px',
            background: '#fafafa'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ margin: 0 }}>📚 Saved Rules ({ruleCount})</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  value={rulesSearch}
                  onChange={(e) => setRulesSearch(e.target.value)}
                  placeholder="Search payee/property/category"
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    padding: '6px 8px',
                    fontSize: 12,
                    minWidth: 240
                  }}
                />
                <button
                  onClick={resetAllBankRules}
                  style={{
                    padding: '6px 12px',
                    fontSize: 12,
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                  title="Delete all rules and start over"
                >
                  🗑️ Reset All Rules
                </button>
              </div>
            </div>

            <div style={{ marginTop: 12, maxHeight: 280, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', fontSize: 12, color: '#4b5563' }}>Payee</th>
                    <th style={{ padding: '8px 10px', fontSize: 12, color: '#4b5563' }}>Category</th>
                    <th style={{ padding: '8px 10px', fontSize: 12, color: '#4b5563' }}>IRS Type (optional)</th>
                    <th style={{ padding: '8px 10px', fontSize: 12, color: '#4b5563' }}>Property</th>
                    <th style={{ padding: '8px 10px', fontSize: 12, color: '#4b5563' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(learningRules)
                    .filter(([key, val]) => {
                      const q = rulesSearch.trim().toLowerCase();
                      if (!q) return true;
                      const display = (val?.displayName || key || '').toString().toLowerCase();
                      const cat = (val?.category || '').toString().toLowerCase();
                      const prop = (val?.propertyName || '').toString().toLowerCase();
                      return display.includes(q) || cat.includes(q) || prop.includes(q);
                    })
                    .map(([key, val]) => (
                      <tr key={key} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 10px', fontSize: 13 }}>
                          <div style={{ fontWeight: 600 }}>{val?.displayName || key}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{key}</div>
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: 13 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <select
                              value={val?.category || ''}
                              onChange={(e) => {
                                const newCat = e.target.value;
                                const updated = { ...learningRules };
                                if (!updated[key]) updated[key] = {};
                                updated[key] = { ...updated[key], category: newCat };
                                setLearningRules(updated);
                              }}
                              style={{
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                padding: '4px 6px',
                                fontSize: 12,
                                minWidth: 160,
                                background: 'white'
                              }}
                            >
                              <option value="">Select...</option>
                              <option value="rental_income">rental_income (treat as income)</option>
                              <option value="w2_income">w2_income (W-2 wages)</option>
                              <option value="travel">travel (expense)</option>
                              <option value="costa_rica_expense">costa_rica_expense</option>
                              <option value="recurring">recurring</option>
                              <option value="new_bill">new_bill</option>
                              <option value="child_expenses">child_expenses</option>
                              <option value="non_income">non_income (not income)</option>
                              <option value="other">other</option>
                            </select>
                            <button
                              onClick={() => saveRuleCategory(key, val, (learningRules[key]?.category || val?.category || ''), (learningRules[key]?.propertyId ?? val?.propertyId ?? null), (learningRules[key]?.irsType ?? val?.irsType ?? null))}
                              style={{
                                fontSize: 12,
                                padding: '6px 10px',
                                background: '#ecfeff',
                                color: '#155e75',
                                borderRadius: 6,
                                border: '1px solid #a5f3fc'
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: 13 }}>
                          {['recurring', 'new_bill', 'child_expenses'].includes(val?.category) ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <select
                                value={val?.irsType ?? ''}
                                onChange={(e) => {
                                  const irsType = e.target.value || null;
                                  const updated = { ...learningRules };
                                  if (!updated[key]) updated[key] = {};
                                  updated[key] = { ...updated[key], irsType };
                                  setLearningRules(updated);
                                }}
                                style={{
                                  border: '1px solid #d1d5db',
                                  borderRadius: 6,
                                  padding: '4px 6px',
                                  fontSize: 12,
                                  minWidth: 180,
                                  background: 'white'
                                }}
                                title="Optional: IRS deduction type for tax forms"
                              >
                                <option value="">Select IRS type (optional)</option>
                                {Object.entries(irsDeductionTypes).map(([k, t]) => (
                                  <option key={k} value={k}>{t.name} ({t.code})</option>
                                ))}
                              </select>
                              <button
                                onClick={() => saveRuleCategory(key, learningRules[key] || val, (learningRules[key]?.category || val?.category || ''), (learningRules[key]?.propertyId ?? val?.propertyId ?? null), (learningRules[key]?.irsType ?? val?.irsType ?? null))}
                                style={{ fontSize: 11, padding: '4px 8px', background: '#ecfeff', color: '#155e75', borderRadius: 6, border: '1px solid #a5f3fc' }}
                              >
                                Save
                              </button>
                            </div>
                          ) : (
                            val?.irsType ? (irsDeductionTypes[val.irsType]?.name || val.irsType) : '—'
                          )}
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: 13 }}>
                          {(val?.category === 'rental_income') ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <select
                                value={val?.propertyId ?? ''}
                                onChange={(e) => {
                                  const propertyId = e.target.value ? parseInt(e.target.value) : null;
                                  const updated = { ...learningRules };
                                  if (!updated[key]) updated[key] = {};
                                  const prop = properties.find(p => p.id === propertyId);
                                  updated[key] = { ...updated[key], propertyId, propertyName: prop?.name || '' };
                                  setLearningRules(updated);
                                }}
                                style={{
                                  border: '1px solid #d1d5db',
                                  borderRadius: 6,
                                  padding: '4px 6px',
                                  fontSize: 12,
                                  minWidth: 160,
                                  background: 'white'
                                }}
                              >
                                <option value="">— Select property —</option>
                                {properties.map(p => (
                                  <option key={p.id} value={p.id}>
                                    {p.name} {p.type === 'primary' ? '(Primary)' : ''}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => saveRuleCategory(key, learningRules[key] || val, (learningRules[key]?.category || val?.category || 'rental_income'), (learningRules[key]?.propertyId ?? val?.propertyId ?? null), (learningRules[key]?.irsType ?? val?.irsType ?? null))}
                                style={{ fontSize: 11, padding: '4px 8px', background: '#ecfeff', color: '#155e75', borderRadius: 6, border: '1px solid #a5f3fc' }}
                              >
                                Save
                              </button>
                            </div>
                          ) : (
                            properties.find(p => p.id === (val?.propertyId))?.name || val?.propertyName || '—'
                          )}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete rule for "${val?.displayName || key}"?`)) return;
                              try {
                                const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
                                await axios.delete(`/api/expenses/zelle-rules/${encodeURIComponent(key)}`, { headers });
                                const updated = { ...learningRules };
                                delete updated[key];
                                setLearningRules(updated);
                                localStorage.setItem('learningRules', JSON.stringify(updated));
                                toast.success('Rule deleted');
                              } catch (e) {
                                console.error('Delete rule error:', e);
                                toast.error('Failed to delete rule: ' + (e?.response?.data?.error || e?.message || 'Unknown error'));
                              }
                            }}
                            style={{
                              fontSize: 12,
                              padding: '6px 10px',
                              background: '#fee2e2',
                              color: '#b91c1c',
                              borderRadius: 6,
                              border: '1px solid #fecaca'
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  {Object.keys(learningRules).length === 0 && (() => {
                    console.log('⚠️ No rules to display - learningRules is empty');
                    return (
                    <tr>
                      <td colSpan={5} style={{ padding: '12px 10px', fontSize: 13, color: '#6b7280' }}>
                        No rules saved yet. Classify transactions to create rules.
                      </td>
                    </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}
      </div>
      
      {/* Property Selection Modal for Rental Income */}
      {rentalIncomePropertySelection && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setRentalIncomePropertySelection(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: 'bold', color: '#166534' }}>
              Select Property for Rental Income
            </h3>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '6px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                Transaction: {rentalIncomePropertySelection.transaction.description}
              </div>
              <div style={{ fontSize: '13px', color: '#666' }}>
                Amount: ${Math.abs(Number(rentalIncomePropertySelection.transaction.amount) || 0).toFixed(2)} | 
                Date: {rentalIncomePropertySelection.transaction.date}
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Choose rental property:
              </label>
              <select
                onChange={(e) => {
                  const propertyId = e.target.value ? parseInt(e.target.value) : null;
                  if (propertyId) {
                    const t = rentalIncomePropertySelection.transaction;
                    const idx = rentalIncomePropertySelection.index;
                    const key = ruleKeyForDescription(t.description || '');
                    const current = learningRules[key] || { displayName: key };
                    const updated = { ...learningRules, [key]: { ...current, category: 'rental_income', propertyId } };
                    setLearningRules(updated);
                    localStorage.setItem('learningRules', JSON.stringify(updated));
                    saveRuleCategory(key, updated[key], 'rental_income', propertyId);
                    
                    // Update the transaction in analysis results
                    if (analysisResults && analysisResults.allTransactions) {
                      const updatedTransactions = [...analysisResults.allTransactions];
                      if (updatedTransactions[idx]) {
                        updatedTransactions[idx] = { ...updatedTransactions[idx], userCategory: 'rental_income', category: 'rental_income', propertyId };
                      } else {
                        // Fallback: find by matching transaction
                        const updated = updatedTransactions.map(tx => {
                          if (tx.description === t.description && tx.date === t.date && tx.amount === t.amount) {
                            return { ...tx, userCategory: 'rental_income', category: 'rental_income', propertyId };
                          }
                          return tx;
                        });
                        setAnalysisResults({
                          ...analysisResults,
                          allTransactions: updated
                        });
                        setRentalIncomePropertySelection(null);
                        toast.success(`Marked as rental income for ${properties.find(p => p.id === propertyId)?.name || 'property'}`);
                        return;
                      }
                      setAnalysisResults({
                        ...analysisResults,
                        allTransactions: updatedTransactions
                      });
                    }
                    
                    setRentalIncomePropertySelection(null);
                    toast.success(`Marked as rental income for ${properties.find(p => p.id === propertyId)?.name || 'property'}`);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: '2px solid #86efac',
                  borderRadius: '6px',
                  backgroundColor: 'white'
                }}
                autoFocus
              >
                <option value="">-- Select a property --</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.type === 'primary' ? '(Primary - not taxable)' : ''} {p.address ? `(${p.address})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRentalIncomePropertySelection(null)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply to Similar Modal - review matches, then Apply & queue */}
      {applySimilarModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => setApplySimilarModal(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '95vw',
              width: '95%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '20px', fontWeight: 'bold', color: '#1e40af' }}>
              📋 Apply to similar — {extractMatchKey(applySimilarModal.sourceTx?.description || '') || 'this payee'}
            </h3>
            <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
              Only lines from this payee are shown. Other merchants on the statement are not affected.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Main category</label>
              <select
                value={applySimilarModal.category || ''}
                onChange={(e) => setApplySimilarModal(prev => ({ ...prev, category: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '200px' }}
              >
                <option value="w2_income">📄 W-2 Income</option>
                <option value="rental_income">🏠 Rental Income</option>
                <option value="costa_rica_expense">🇨🇷 Costa Rica Expense</option>
                <option value="travel">✈️ Travel</option>
                <option value="recurring">🔄 Recurring Expense</option>
                <option value="new_bill">📋 New Bill</option>
                <option value="child_expenses">👶 Child Expenses</option>
                <option value="atm_withdrawal">🏧 ATM Withdrawal</option>
                <option value="savings">💰 Savings</option>
                <option value="other">❓ Other</option>
              </select>
            </div>

            {/* Property - for rental_income, recurring, new_bill */}
            {(applySimilarModal.category === 'rental_income' || applySimilarModal.category === 'recurring' || applySimilarModal.category === 'new_bill') && propertiesLoaded && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                  {applySimilarModal.category === 'rental_income' ? 'Property (rental income)' : 'Property (which house)'}
                </label>
                <select
                  value={applySimilarModal.propertyId || ''}
                  onChange={(e) => setApplySimilarModal(prev => ({ ...prev, propertyId: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '220px' }}
                >
                  <option value="">Select property</option>
                  {properties && properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} - {p.address}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Recurring type & frequency - for recurring */}
            {applySimilarModal.category === 'recurring' && (
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Recurring type</label>
                  <select
                    value={applySimilarModal.recurringType || ''}
                    onChange={(e) => setApplySimilarModal(prev => ({ ...prev, recurringType: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '180px' }}
                  >
                    <option value="">Select type</option>
                    <option value="recurring_mortgage">🏠 Mortgage Payment</option>
                    <option value="recurring_loan">💰 Loan Payment</option>
                    <option value="recurring_gas">⛽ Gas/Utility Bill</option>
                    <option value="recurring_insurance">🛡️ Insurance</option>
                    <option value="recurring_phone">📱 Phone/Internet</option>
                    <option value="recurring_subscription">📺 Subscription Service</option>
                    <option value="recurring_credit_card">💳 Credit Card Payment</option>
                    <option value="recurring_property_taxes">🏛️ Property Taxes</option>
                    <option value="recurring_other">🔄 Other Recurring</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Frequency</label>
                  <select
                    value={applySimilarModal.recurringFrequency || 'monthly'}
                    onChange={(e) => setApplySimilarModal(prev => ({ ...prev, recurringFrequency: e.target.value }))}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '140px' }}
                  >
                    <option value="monthly">📅 Monthly</option>
                    <option value="quarterly">📆 Quarterly</option>
                    <option value="semi_annually">📈 Semi-Annually</option>
                    <option value="yearly">🗓️ Annually</option>
                  </select>
                </div>
              </div>
            )}

            {/* Savings Account - for savings */}
            {applySimilarModal.category === 'savings' && accountsLoaded && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                  Savings Account (Required)
                </label>
                <select
                  value={applySimilarModal.savingsAccount || ''}
                  onChange={(e) => setApplySimilarModal(prev => ({ ...prev, savingsAccount: e.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '220px' }}
                >
                  <option value="">Select Savings Account</option>
                  {accounts && accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name} - {a.account_type} ({a.bank_name})</option>
                  ))}
                </select>
              </div>
            )}

            {/* IRS deduction type - for recurring, new_bill, or child_expenses (tax deductible) */}
            {(applySimilarModal.category === 'recurring' || applySimilarModal.category === 'new_bill' || applySimilarModal.category === 'child_expenses') && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>IRS deduction type (tax deductible)</label>
                <select
                  value={
                    applySimilarModal.category === 'recurring' ? (applySimilarModal.recurringIrsType || '') :
                    applySimilarModal.category === 'new_bill' ? (applySimilarModal.newBillIrsType || '') :
                    (applySimilarModal.childIrsType || '')
                  }
                  onChange={(e) => setApplySimilarModal(prev => ({
                    ...prev,
                    ...(prev.category === 'recurring' ? { recurringIrsType: e.target.value } :
                       prev.category === 'new_bill' ? { newBillIrsType: e.target.value } :
                       { childIrsType: e.target.value })
                  }))}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '240px' }}
                >
                  <option value="">Select IRS deduction type</option>
                  {Object.entries(irsDeductionTypes).map(([key, irsType]) => (
                    <option key={key} value={key}>{irsType.name} ({irsType.code})</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginBottom: '20px', maxHeight: '300px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', minWidth: '500px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                <thead style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Date</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', minWidth: '180px' }}>Description</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Amount</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', width: '90px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {applySimilarModal.matches.map((m, i) => {
                    const idx = applySimilarModal.indices[i];
                    const isRemoved = (applySimilarModal.removedIndices || new Set()).has(idx);
                    if (isRemoved) return null;
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '10px 8px', fontSize: '13px', whiteSpace: 'nowrap' }}>{m.date}</td>
                        <td style={{ padding: '10px 8px', fontSize: '13px', wordBreak: 'break-word' }}>{m.description}</td>
                        <td style={{ padding: '10px 8px', fontSize: '13px', fontWeight: '500', textAlign: 'right', whiteSpace: 'nowrap' }}>${Math.abs(m.amount).toFixed(2)}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => setApplySimilarModal(prev => ({
                              ...prev,
                              removedIndices: new Set([...(prev.removedIndices || []), idx])
                            }))}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              color: '#991b1b',
                              cursor: 'pointer'
                            }}
                            title="Remove from this batch (in case you have two different recurring with same amount)"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: '#6b7280', marginRight: '8px' }}>
                {applySimilarModal.matches.length - (applySimilarModal.removedIndices?.size || 0)} of {applySimilarModal.matches.length} selected
              </span>
              <button
                onClick={() => setApplySimilarModal(null)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyAndQueue}
                disabled={(applySimilarModal.matches.length - (applySimilarModal.removedIndices?.size || 0)) === 0}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: (applySimilarModal.matches.length - (applySimilarModal.removedIndices?.size || 0)) === 0 ? '#9ca3af' : '#10b981',
                  border: '1px solid ' + ((applySimilarModal.matches.length - (applySimilarModal.removedIndices?.size || 0)) === 0 ? '#9ca3af' : '#059669'),
                  borderRadius: '6px',
                  color: 'white',
                  cursor: (applySimilarModal.matches.length - (applySimilarModal.removedIndices?.size || 0)) === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: '500'
                }}
              >
                Apply to this payee ({applySimilarModal.matches.length - (applySimilarModal.removedIndices?.size || 0)})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Review Modal */}
      {duplicateReviewModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => setDuplicateReviewModal(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '95vw',
              width: '95%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '20px', fontWeight: 'bold', color: '#dc2626' }}>
              Transactions Already in Database
            </h3>
            <p style={{ marginBottom: '8px', color: '#666' }}>
              These transactions are already saved. Click <strong>Remove from import</strong> to exclude them from this batch, or <strong>Continue</strong> to skip re-importing (they will not be saved again).
            </p>
            <p style={{ marginBottom: '20px', fontSize: '13px', color: '#059669', backgroundColor: '#ecfdf5', padding: '8px 12px', borderRadius: '6px' }}>
              💡 Tip: On the Expenses page, set the Tax Year filter to <strong>All Years</strong> to see imported transactions from any year.
            </p>
            
            <div style={{ marginBottom: '20px', maxHeight: '400px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', minWidth: '600px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                <thead style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Date</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', minWidth: '200px' }}>Description</th>
                    <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Amount</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Existing As</th>
                    <th style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap', width: '120px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateReviewModal.duplicates.map((dup, index) => {
                    if (dup._deleted) return null; // Skip deleted items
                    const existingAmount = dup.existing.amount ? `$${Math.abs(dup.existing.amount).toFixed(2)}` : 'N/A';
                    return (
                      <tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px 8px', fontSize: '13px', whiteSpace: 'nowrap' }}>{dup.transaction.date}</td>
                        <td style={{ padding: '12px 8px', fontSize: '13px', wordBreak: 'break-word' }}>{dup.transaction.description}</td>
                        <td style={{ padding: '12px 8px', fontSize: '13px', fontWeight: '500', textAlign: 'right', whiteSpace: 'nowrap' }}>${Math.abs(dup.transaction.amount).toFixed(2)}</td>
                        <td style={{ padding: '12px 8px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {dup.type} ({existingAmount})
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => {
                              // Mark as deleted
                              const updatedDuplicates = [...duplicateReviewModal.duplicates];
                              updatedDuplicates[index] = { ...updatedDuplicates[index], _deleted: true };
                              setDuplicateReviewModal({
                                ...duplicateReviewModal,
                                duplicates: updatedDuplicates
                              });
                            }}
                            style={{
                              padding: '6px 14px',
                              fontSize: '13px',
                              background: '#fee2e2',
                              border: '2px solid #fecaca',
                              borderRadius: '6px',
                              color: '#991b1b',
                              cursor: 'pointer',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Remove from import
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {duplicateReviewModal.duplicates.filter(d => !d._deleted).length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  All duplicates have been removed. Click "Continue" to save remaining transactions.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDuplicateReviewModal(null)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  // Remove deleted duplicates from transactionsToSave
                  const deletedIndices = new Set();
                  duplicateReviewModal.duplicates.forEach((dup, idx) => {
                    if (dup._deleted) {
                      // Find the matching transaction in transactionsToSave
                      const matchingIndex = duplicateReviewModal.transactionsToSave.findIndex(tx => 
                        tx.date === dup.transaction.date &&
                        tx.description === dup.transaction.description &&
                        Math.abs(tx.amount - dup.transaction.amount) < 0.01
                      );
                      if (matchingIndex !== -1) {
                        deletedIndices.add(matchingIndex);
                      }
                    }
                  });

                  // Filter out deleted transactions
                  const filteredTransactions = duplicateReviewModal.transactionsToSave.filter((_, idx) => !deletedIndices.has(idx));
                  
                  // Close modal
                  setDuplicateReviewModal(null);
                  
                  // Proceed with save
                  if (filteredTransactions.length > 0) {
                    await proceedWithSaveAfterDuplicateReview(filteredTransactions, duplicateReviewModal.headers);
                  } else {
                    toast('All transactions were removed. Nothing to save.');
                  }
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: '#10b981',
                  border: '1px solid #059669',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Continue ({duplicateReviewModal.duplicates.filter(d => !d._deleted).length} remaining)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankStatementAnalyzer;
